import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconCalendarCog, IconCalendarPlus, IconMapPin, IconTicket } from '@tabler/icons-react';
import OpenOsV2EmailModal from './OpenOsV2EmailModal';
import OpenOsV2ContratoVencModal from './OpenOsV2ContratoVencModal';
import OpenOsV2RecentOsModal from './OpenOsV2RecentOsModal';
import OpenOsV2AgendaRouteMap from './OpenOsV2AgendaRouteMap';
import { osServiceBadge } from './openOsV2RouteHelpPins';
import OpenOsV2HistOsGoMedia, { fetchOsGoAtendimento } from '../components/OpenOsV2HistOsGoMedia';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import OpenOsV2ProdStickNote from '../components/OpenOsV2ProdStickNote';
import '../components/OpenOsV2ProductionChart.css';
import useCutoverActive from '../../pg-native/useCutoverActive';

function recurrenceTypePhrase(labels) {
  const list = (labels || []).filter(Boolean);
  if (list.length <= 1) return list[0] || 'um dos tipos marcados na configuração';
  return `${list.slice(0, -1).join(', ')} ou ${list[list.length - 1]}`;
}

function css(s) {
  if (!s || typeof s !== 'string') return undefined;
  const o = {};
  s.split(';').forEach((part) => {
    const i = part.indexOf(':');
    if (i < 0) return;
    const k = part.slice(0, i).trim();
    const val = part.slice(i + 1).trim();
    if (!k) return;
    const camel = k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    o[camel] = val;
  });
  return o;
}

function UltimoObsTipPortal({ open, anchorRef, observacao, motivo }) {
  const tipRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const place = () => {
      const wrap = anchorRef && anchorRef.current;
      const btn = wrap && wrap.querySelector('.oosv2-ultimo-obs-btn');
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const width = Math.min(280, window.innerWidth - 16);
      const pad = 8;
      let left = r.right - width;
      left = Math.max(pad, Math.min(left, window.innerWidth - width - pad));
      const h = (tipRef.current && tipRef.current.offsetHeight) || 160;
      let top = r.bottom + 8;
      let above = false;
      if (top + h + pad > window.innerHeight) {
        top = Math.max(pad, r.top - h - 8);
        above = top + h <= r.top;
      }
      const arrow = Math.max(8, Math.min(width - 18, r.left + r.width / 2 - left - 5));
      setPos({ top, left, width, arrow, above });
    };
    place();
    const id = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, observacao, motivo, anchorRef]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      ref={tipRef}
      className={'oosv2-ultimo-obs-tip is-portal' + (pos.above ? ' is-above' : '')}
      role="tooltip"
      style={{
        top: pos.top,
        left: pos.left,
        width: pos.width,
        ['--arrow-right']: pos.arrow + 'px',
      }}
    >
      <div className="oosv2-ultimo-obs-tip-kicker">Observação</div>
      <div className="oosv2-ultimo-obs-tip-text">{observacao}</div>
      <div className="oosv2-ultimo-obs-tip-kicker">Motivo</div>
      <div className="oosv2-ultimo-obs-tip-text">{motivo}</div>
    </div>,
    document.body
  );
}

function DefeitoTipoOsBadge({ sigla, label }) {
  const wrapRef = useRef(null);
  const [tip, setTip] = useState(null);

  useEffect(() => {
    if (!tip) return undefined;
    const hide = () => setTip(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [tip]);

  const show = () => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setTip({
      text: label,
      top: r.top - 6,
      left: r.left + r.width / 2,
    });
  };

  return (
    <>
      <span
        ref={wrapRef}
        className="oosv2-defeito-tipo-badge"
        aria-label={label}
        onMouseEnter={show}
        onMouseLeave={() => setTip(null)}
        onFocus={show}
        onBlur={() => setTip(null)}
        tabIndex={0}
      >
        {sigla}
      </span>
      {tip
        ? createPortal(
            <div
              className="oosv2-defeito-tipo-badge-tip"
              style={{ top: tip.top, left: tip.left }}
              role="tooltip"
            >
              {tip.text}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function SiteNameWithAddress({ nome, endereco }) {
  const wrapRef = useRef(null);
  const [tip, setTip] = useState(null);
  const addr = String(endereco || '').trim();

  useEffect(() => {
    if (!tip) return undefined;
    const hide = () => setTip(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [tip]);

  const show = () => {
    if (!addr || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setTip({
      text: addr,
      top: Math.max(8, r.top - 8),
      left: r.left + Math.min(r.width / 2, 200),
    });
  };

  return (
    <>
      <div
        ref={wrapRef}
        className="oosv2-site-name"
        onMouseEnter={show}
        onMouseLeave={() => setTip(null)}
      >
        <IconMapPin className="oosv2-site-pin" size={15} stroke={1.7} aria-hidden />
        <span className="oosv2-site-name-text">{nome}</span>
      </div>
      {tip
        ? createPortal(
            <div
              className="oosv2-site-addr-tip"
              style={{ top: tip.top, left: tip.left }}
              role="tooltip"
            >
              {tip.text}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

/** Reproduz o `style-hover` do dc-runtime: mescla o estilo extra no hover. */
function Hover({ as: Tag = 'div', baseStyle, hoverStyle, children, ...rest }) {
  const [on, setOn] = useState(false);
  const merged = on ? { ...css(baseStyle), ...css(hoverStyle) } : css(baseStyle);
  return (
    <Tag
      {...rest}
      style={merged}
      onMouseEnter={() => setOn(true)}
      onMouseLeave={() => setOn(false)}
    >
      {children}
    </Tag>
  );
}

function MirrorOrderRow({ p, showTooltip, hideTooltip }) {
  const badge = osServiceBadge(p);
  const os = String(p.os || '').trim();
  const cliente = String(p.cliente || '').trim();
  const bairro = String(p.bairro || '').trim();
  const isRetorno = !!String(p.condtReturn || p.condt_return || '').trim();
  const pedidoVinculado = p.pedidoVinculado;
  const rowClass = [
    'technician-order-row',
    badge.alert ? 'technician-order-row--defeito-alert' : '',
    isRetorno ? 'order-status-retorno' : '',
    pedidoVinculado ? 'has-pedido-vinculado' : '',
    'board-mirror-row',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rowClass}>
      <div className="technician-order-cell technician-order-id">
        <span
          className={
            'service-type-badge-compact' +
            (badge.alert ? ' service-type-badge-compact--defeito-alert' : '') +
            ' board-cell-tooltip-trigger'
          }
          style={{ backgroundColor: badge.bg }}
          onMouseEnter={(e) => showTooltip(e, p, 'badge')}
          onMouseLeave={hideTooltip}
        >
          {badge.text}
        </span>
        <span
          className="order-id-text board-cell-tooltip-trigger"
          onMouseEnter={(e) => showTooltip(e, p, 'os')}
          onMouseLeave={hideTooltip}
        >
          {os}
        </span>

        {isRetorno ? (
          <span
            className="order-retorno-icon-wrap order-retorno-icon-wrap--technician board-cell-tooltip-trigger"
            onMouseEnter={(e) => showTooltip(e, p, 'retorno')}
            onMouseLeave={hideTooltip}
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <i className="bi bi-bell order-retorno-icon" aria-label="Retorno" />
          </span>
        ) : null}

        {pedidoVinculado ? (
          <span
            className="board-cell-tooltip-trigger order-pedido-vinc-icon-wrap"
            style={{ display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={(e) => showTooltip(e, p, 'pedidoVinculado')}
            onMouseLeave={hideTooltip}
          >
            <i className="bi bi-box-fill pedido-vinculado-icon" aria-hidden />
          </span>
        ) : null}
      </div>
      <div
        className="technician-order-cell technician-order-cliente board-cell-tooltip-trigger"
        onMouseEnter={(e) => showTooltip(e, p, 'cliente')}
        onMouseLeave={hideTooltip}
      >
        {cliente}
      </div>
      <div
        className="technician-order-cell technician-order-equipamento board-cell-tooltip-trigger"
        onMouseEnter={(e) => showTooltip(e, p, 'equipamento')}
        onMouseLeave={hideTooltip}
      >
        {bairro}
      </div>
    </div>
  );
}

function MirrorDaySection({ title, dataGroup, groups, showTooltip, hideTooltip }) {
  const total = groups.reduce((s, g) => s + g.items.length, 0);
  if (total === 0) return null;
  return (
    <div className="technician-service-group" data-group={dataGroup}>
      <div className="service-group-header">
        <div className="service-group-title">{title}</div>
        <div className="service-group-count">{total}</div>
      </div>
      <div className="technician-city-orders">
        {groups.map((g) => (
          <div className="technician-city-group" key={g.bairro}>
            <div className="technician-city-header">
              <div className="technician-city-title">{g.bairro}</div>
              <div className="technician-city-count">{g.items.length}</div>
            </div>
            <div className="technician-city-order">
              {g.items.map((p) => (
                <MirrorOrderRow key={p.os} p={p} showTooltip={showTooltip} hideTooltip={hideTooltip} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MirrorCondtBlock({ title, dataGroup, points, showTooltip, hideTooltip }) {
  const groups = mirrorGroupByBairro(points);
  const total = groups.reduce((s, g) => s + g.items.length, 0);
  return (
    <div className="technician-service-group" data-group={dataGroup}>
      <div className="service-group-header">
        <div className="service-group-title">{title}</div>
        <div className="service-group-count">{total}</div>
      </div>
      <div className="technician-city-orders">
        {total === 0 ? (
          <div style={{ fontSize: 10, color: '#8a8a9e', fontWeight: 700, padding: '10px 6px 6px' }}>
            Sem ordens
          </div>
        ) : null}
        {total > 0
          ? groups.map((g) => (
              <div className="technician-city-group" key={g.bairro}>
                <div className="technician-city-header">
                  <div className="technician-city-title">{g.bairro}</div>
                  <div className="technician-city-count">{g.items.length}</div>
                </div>
                <div className="technician-city-order">
                  {g.items.map((p) => (
                    <MirrorOrderRow key={p.os} p={p} showTooltip={showTooltip} hideTooltip={hideTooltip} />
                  ))}
                </div>
              </div>
            ))
          : null}
      </div>
    </div>
  );
}

function mirrorGroupByBairro(pts) {
  const map = new Map();
  (pts || []).forEach((p) => {
    if (!p) return;
    const b = String(p.bairro || '').trim() || '—';
    if (!map.has(b)) map.set(b, []);
    map.get(b).push(p);
  });
  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'))
    .map(([bairro, arr]) => ({
      bairro,
      items: arr.sort((a, b) => String(a.os || '').localeCompare(String(b.os || ''), 'pt-BR', { numeric: true })),
    }));
}

function OpenOsV2AgendaTechMirror({ technicianName, points, technicianObj }) {
  const [cellTooltip, setCellTooltip] = useState(null);

  const tech = String(technicianName || '').trim();
  const atTech = tech ? `@${tech}` : '@—';
  const safePoints = Array.isArray(points) ? points : [];
  const pointsCodTecKey = safePoints.length > 0 ? String(safePoints[0]?.codTec || '').trim() : '';

  const [futuraPendentePoints, setFuturaPendentePoints] = useState([]);
  const [futuraLiberadoPoints, setFuturaLiberadoPoints] = useState([]);
  const [futuraLoading, setFuturaLoading] = useState(false);
  const [futuraError, setFuturaError] = useState('');

  useEffect(() => {
    const rawFromPoints = pointsCodTecKey;

    const rawId = rawFromPoints || String(
      (technicianObj && technicianObj.codigo != null ? technicianObj.codigo : '') ||
      (technicianObj && technicianObj.id != null ? technicianObj.id : '')
    ).trim();
    if (!rawId) {
      setFuturaPendentePoints([]);
      setFuturaLiberadoPoints([]);
      setFuturaError('');
      setFuturaLoading(false);
      return undefined;
    }

    const digits = rawId.replace(/\D/g, '');
    const padded = digits ? digits.padStart(4, '0').slice(-4) : rawId;
    const stripped = digits ? digits.replace(/^0+/, '') || padded : rawId;
    const keysToTry = [...new Set([padded, stripped, rawId])];

    const token = typeof window !== 'undefined' ? window.localStorage.getItem('authToken') : null;
    if (!token) return undefined;

    const alive = { v: true };
    setFuturaLoading(true);
    setFuturaError('');

    fetch('/api/orders/technicians/batch', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ technicianIds: keysToTry }),
    })
      .then((r) => r.json())
      .then((json) => {
        if (!alive.v) return;
        if (!json || !json.success) {
          throw new Error(json?.message || 'Falha ao carregar FUTURA (condt)');
        }

        let techData = null;
        if (json.data) {
          for (const k of keysToTry) {
            if (json.data[k]) { techData = json.data[k]; break; }
          }
          if (!techData) {
            const allKeys = Object.keys(json.data);
            if (allKeys.length === 1) techData = json.data[allKeys[0]];
          }
        }

        const pendOrders = Array.isArray(techData?.PENDENTE) ? techData.PENDENTE : [];
        const libOrders = Array.isArray(techData?.LIBERADO) ? techData.LIBERADO : [];

        const mapCondtOrderToPoint = (o) => {
          const os = String(o?.TB02115_CODIGO ?? o?.id ?? '').trim();
          const cliente = String(o?.cliente ?? o?.TB01008_NOME ?? '').trim();
          const equipamento = String(
            o?.equipamento ?? o?.TB01010_RESUMIDO ?? o?.TB01010_NOME ?? o?.cidade ?? ''
          ).trim();
          const defeitoAlert = String(o?.defeito_alert ?? o?.defeitoAlert ?? '').trim();
          const colorBg = String(o?.color_bg ?? o?.colorBg ?? '').trim();
          const preventiva = String(o?.preventiva ?? o?.TB02115_PREVENTIVA ?? o?.tipo ?? '').trim();

          const condtReturn = String(o?.condt_return ?? o?.condtReturn ?? '').trim();
          const condt_requests = o?.condt_requests ?? o?.condtRequests;
          const condt_requests_available = o?.condt_requests_available ?? o?.condtRequestsAvailable;
          const pedidoVinculado = o?.pedidoVinculado ?? o?.PEDIDO_VINCULADO ?? null;

          if (!os) return null;
          return {
            os,
            cliente,
            bairro: equipamento || '—',
            defeitoAlert,
            colorBg,
            preventiva,
            condtReturn,
            condt_requests,
            condt_requests_available,
            pedidoVinculado: pedidoVinculado != null ? String(pedidoVinculado).trim() : null,
          };
        };

        setFuturaPendentePoints(pendOrders.map(mapCondtOrderToPoint).filter(Boolean));
        setFuturaLiberadoPoints(libOrders.map(mapCondtOrderToPoint).filter(Boolean));
      })
      .catch((err) => {
        if (!alive.v) return;
        setFuturaError(String(err?.message || 'Falha ao carregar FUTURA (condt)'));
        setFuturaPendentePoints([]);
        setFuturaLiberadoPoints([]);
      })
      .finally(() => {
        if (!alive.v) return;
        setFuturaLoading(false);
      });

    return () => {
      alive.v = false;
    };
  }, [technicianObj, pointsCodTecKey]);

  const hojePoints = React.useMemo(() => safePoints.filter((p) => p.day !== 'amanha'), [safePoints]);
  const amanhaPoints = React.useMemo(() => safePoints.filter((p) => p.day === 'amanha'), [safePoints]);
  const hojeGroups = React.useMemo(() => mirrorGroupByBairro(hojePoints), [hojePoints]);
  const amanhaGroups = React.useMemo(() => mirrorGroupByBairro(amanhaPoints), [amanhaPoints]);

  const hideTooltip = () => setCellTooltip(null);
  const showTooltip = (e, point, kind) => {
    if (!e?.currentTarget || !point) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = r.left + r.width / 2;
    const y = r.bottom - 4;
    const badge = osServiceBadge(point);
    const os = String(point.os || '').trim() || 'N/A';
    const cliente = String(point.cliente || '').trim() || 'N/A';
    const bairro = String(point.bairro || '').trim() || 'N/A';
    let text = '';
    if (kind === 'badge') {
      text = badge.alert
        ? `Alerta: ${badge.text}\nOS: ${os}\nCliente: ${cliente}\nBairro: ${bairro}`
        : `Tipo OS: ${badge.text}\nOS: ${os}\nCliente: ${cliente}\nBairro: ${bairro}`;
    } else if (kind === 'os') {
      text = `OS: ${os}\nCliente: ${cliente}\nBairro: ${bairro}`;
    } else if (kind === 'retorno') {
      const cr = String(point.condtReturn || point.condt_return || '').trim();
      const rq = point.condt_requests != null ? String(point.condt_requests) : String(point.condt_requests || '');
      const rav = point.condt_requests_available != null ? String(point.condt_requests_available) : String(point.condt_requests_available || '');
      text = `Retorno\ncondt_return: ${cr || '—'}\ncondt_requests: ${rq || '—'}\ncondt_requests_available: ${rav || '—'}`;
    } else if (kind === 'pedidoVinculado') {
      text = `Pedido vinculado: ${String(point.pedidoVinculado || '').trim() || '—'}`;
    } else if (kind === 'cliente') {
      text = `Cliente: ${cliente}\nOS: ${os}\nBairro: ${bairro}`;
    } else {
      text = `Bairro: ${bairro}\nOS: ${os}\nCliente: ${cliente}`;
    }
    setCellTooltip({ x, y, text });
  };

  return (
    <div className="oosv2-agenda-tech-mirror-col">
      <div className="oosv2-agenda-tech-mirror-head">Agenda de {atTech}</div>
      <div
        className="oosv2-agenda-tech-mirror-body"
        style={{ pointerEvents: 'auto' }}
        onMouseLeave={hideTooltip}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <MirrorDaySection
          title="PREVISTO PARA HOJE"
          dataGroup="Previsto para hoje"
          groups={hojeGroups}
          showTooltip={showTooltip}
          hideTooltip={hideTooltip}
        />
        <MirrorDaySection
          title="PREVISTAS PARA AMANHÃ"
          dataGroup="Previstas para amanhã"
          groups={amanhaGroups}
          showTooltip={showTooltip}
          hideTooltip={hideTooltip}
        />

        {futuraLoading ? (
          <div style={{ fontSize: 12, color: '#8a8a9e', fontWeight: 700, padding: '8px 6px' }}>
            Carregando Pendente/Liberado…
          </div>
        ) : null}
        {futuraError ? (
          <div style={{ fontSize: 12, color: '#c53030', fontWeight: 800, padding: '8px 6px' }}>
            {futuraError}
          </div>
        ) : null}
        {!futuraLoading && !futuraError ? (
          <>
            <MirrorCondtBlock
              title="PENDENTE"
              dataGroup="PENDENTE"
              points={futuraPendentePoints}
              showTooltip={showTooltip}
              hideTooltip={hideTooltip}
            />
            <MirrorCondtBlock
              title="LIBERADO"
              dataGroup="LIBERADO"
              points={futuraLiberadoPoints}
              showTooltip={showTooltip}
              hideTooltip={hideTooltip}
            />
          </>
        ) : null}

        {safePoints.length === 0 ? (
          <div style={{ fontSize: 12, color: '#8a8a9e', fontWeight: 700, padding: 16 }}>
            Nenhuma OS no raio para este técnico.
          </div>
        ) : null}

        {cellTooltip
          ? createPortal(
              <div
                className="board-cell-tooltip-portal"
                style={{
                  position: 'fixed',
                  left: cellTooltip.x,
                  top: cellTooltip.y,
                  transform: 'translateX(-50%)',
                  zIndex: 100002,
                  pointerEvents: 'none',
                }}
              >
                <span className="board-cell-tooltip-arrow" />
                <span className="board-cell-tooltip-text">{cellTooltip.text}</span>
              </div>,
              document.body
            )
          : null}
      </div>
    </div>
  );
}

function OpenOsV2AgendaDialog({ v }) {
  if (!v.agendaModal) return null;
  return (
    <div style={css('position:fixed;inset:0;background:rgba(15,15,28,.5);display:flex;align-items:center;justify-content:center;padding:24px;z-index:50')}>
      <div className={'oosv2-agenda-dialog' + (v.routeHelpOpen ? ' is-route' : '') + (v.routeHelpAgendaMirrorOpen ? ' is-mirror' : '')} style={css('max-width:100%;max-height:86vh;overflow:hidden;border-radius:16px;animation:popIn .18s ease both;display:flex;flex-direction:column')}>
        <div className="oosv2-agenda-head" style={css('display:flex;align-items:center;gap:10px;padding:8px 15px 4px')}>
          <div className="oosv2-agenda-head-title" style={css('font-size:13px;font-weight:700')}>{v.agendaDialogTitle || 'Programar atendimento'}</div>
          <div style={css('flex:1')}></div>
          <button type="button" className="oosv2-flow-close oosv2-flow-close--lg" onClick={v.fecharAgenda} aria-label="Fechar">×</button>
        </div>
        <div className={'oosv2-agenda-body' + (v.routeHelpOpen ? ' is-route' : '') + (v.routeHelpAgendaMirrorOpen ? ' is-mirror' : '')} style={css('padding:6px 15px 8px;min-height:0;flex:1')}>
          <div className="oosv2-agenda-main">
          <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700;margin-bottom:8px')}>Quando</div>
          <div style={css(`display:grid;grid-template-columns:repeat(${v.hideApenasTecnico ? 3 : 4},minmax(0,1fr));gap:8px`)}>

            {v.hideApenasTecnico ? null : (
            <div onClick={v.quandoTecnico.pick} className={v.quandoTecnico.className} style={css(v.quandoTecnico.style)}>
              <div className={v.quandoTecnico.iconClassName} style={css(v.quandoTecnico.iconStyle)}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="6.6" r="3.1" stroke="currentColor" strokeWidth="1.5"></circle><path d="M4 16.4c.7-2.8 3-4.4 6-4.4s5.3 1.6 6 4.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path></svg>
              </div>
              <div style={css('font-size:11.5px;font-weight:700;text-align:center;line-height:1.25')}>Apenas definir<br />técnico</div>
            </div>
            )}

            <div onClick={v.quandoHoje.pick} className={v.quandoHoje.className} style={css(v.quandoHoje.style)}>
              <div className={v.quandoHoje.dayClassName} style={css(v.quandoHoje.dayStyle)}>{v.quandoHoje.dia}</div>
              <div className={v.quandoHoje.dowClassName} style={css(v.quandoHoje.dowStyle)}>{v.quandoHoje.dow}</div>
              <div style={css('font-size:11.5px;font-weight:700')}>Hoje</div>
            </div>

            <div onClick={v.quandoAmanha.pick} className={v.quandoAmanha.className} style={css(v.quandoAmanha.style)}>
              <div className={v.quandoAmanha.dayClassName} style={css(v.quandoAmanha.dayStyle)}>{v.quandoAmanha.dia}</div>
              <div className={v.quandoAmanha.dowClassName} style={css(v.quandoAmanha.dowStyle)}>{v.quandoAmanha.dow}</div>
              <div style={css('font-size:11.5px;font-weight:700')}>Amanhã</div>
            </div>

            <div onClick={v.quandoFuturo.pick} className={v.quandoFuturo.className} style={css(v.quandoFuturo.style)}>
              <div className={v.quandoFuturo.iconClassName} style={css(v.quandoFuturo.iconStyle)}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><rect x="2.8" y="4" width="14.4" height="13.2" rx="2.6" stroke="currentColor" strokeWidth="1.5"></rect><path d="M2.8 8h14.4M6.8 2.4v2.8M13.2 2.4v2.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path><circle cx="13.4" cy="12.6" r="1.2" fill="currentColor"></circle></svg>
              </div>
              <div style={css('font-size:11.5px;font-weight:700;text-align:center;line-height:1.25')}>{v.quandoFuturo.legenda}</div>
            </div>
          </div>

          {v.pedirData ? (
            <div style={css('margin-top:11px;animation:popIn .16s ease both')}>
              <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700;margin-bottom:5px')}>Previsão de atendimento</div>
              <div className="oosv2-agenda-date-row">
                <button type="button" className={v.dataPickerBtnClass} data-oosv2-future-date="1">{v.dataDraftLabel}</button>
                <label className="oosv2-agenda-check-label" onClick={v.toggleSemPrevisao}>
                  <span className={v.checkSemPrevisaoClass} aria-hidden="true"></span>
                  <span>Não há data certa de previsão</span>
                </label>
              </div>
              {v.mostrarObsFuturo ? (
                <div style={css('margin-top:10px')}>
                  <div style={css('font-size:11px;font-weight:600;color:#5c5c70;margin-bottom:6px')}>Observações sobre a previsão <span style={css('color:#c53030')}>*</span> (mín. 10 caracteres)</div>
                  <div className="oosv2-agenda-notes-wrap">
                    <textarea className={v.standbyNotesAreaClass} rows="3" placeholder="Descreva o contexto do agendamento futuro — ou pressione espaço para a Orya te ajudar ✨" data-orya-context="Observação sobre previsão de atendimento futuro sem data certa (OS encaminhada sem previsão)." value={v.standbyNotes} onChange={v.setStandbyNotes} />
                    <div className="oosv2-obs-tools oosv2-agenda-notes-tools">
                      <button type="button" className="oosv2-obs-tool-btn" data-oosv2-agenda-voice="1" aria-label="Fala para texto">
                        <span className="oosv2-obs-tool-tip">Grave em voz e tenha o texto transcrito aqui</span>
                        <svg className="oosv2-obs-tool-ico" width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="6" y="2" width="4" height="7" rx="2" stroke="currentColor" strokeWidth="1.5"></rect><path d="M4 8a4 4 0 008 0M8 12v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path></svg>
                        <span>Fala para texto</span>
                      </button>
                    </div>
                  </div>
                  {v.standbyNotesErro ? (
                    <div style={css('font-size:10px;color:#c53030;margin-top:4px;font-weight:600')}>{v.standbyNotesFalta}</div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="oosv2-agenda-tech-head">
            <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Técnico</div>
            <div className="oosv2-agenda-route-wrap">
              <button
                type="button"
                className={'oosv2-agenda-route-btn' + (v.routeHelpOpen ? ' is-on' : '')}
                disabled={!v.routeHelpEnabled}
                title={v.routeHelpEnabled ? 'Ajuda para roteirizar' : 'Disponível com agenda Hoje ou Amanhã'}
                onClick={v.toggleRouteHelp}
              >
                <span className="oosv2-agenda-route-ico" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M12 18.5l-3 -1.5l-6 3v-13l6 -3l6 3l6 -3v7.5" />
                    <path d="M9 4v13" />
                    <path d="M15 7v5.5" />
                    <path d="M21.121 20.121a3 3 0 1 0 -4.242 0c.418 .419 1.125 1.045 2.121 1.879c1.051 -.89 1.759 -1.516 2.121 -1.879" />
                    <path d="M19 18v.01" />
                  </svg>
                </span>
                Ajuda para roteirizar
              </button>
              {v.routeHelpOpen ? (
                <button type="button" className="oosv2-agenda-route-clear" onClick={v.cancelarRouteHelp}>Limpar</button>
              ) : null}
            </div>
            <div className={v.agendaSearchClass}>
              <span className="oosv2-agenda-search-icon" aria-hidden="true">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="6.25" stroke="currentColor" strokeWidth="1.75"></circle><path d="M16.2 16.2L20.5 20.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"></path></svg>
              </span>
              <input type="text" className="oosv2-agenda-search-input" value={v.agendaTechQuery} onChange={v.setAgendaTechQuery} placeholder="Pesquisar..." aria-label="Pesquisar áreas e técnicos" />
            </div>
          </div>
          <div className={v.agendaTechsClass}>
            {v.scheduleAreasLoading ? (
              <div style={css('padding:28px 16px;display:flex;align-items:center;justify-content:center;gap:10px;color:#8a8a9e;font-size:12px;font-weight:600')}>
                <div style={css('width:18px;height:18px;border:2px solid #6d3ef5;border-top-color:transparent;border-radius:50%;animation:spin .8s linear infinite')}></div>
                Carregando áreas e técnicos…
              </div>
            ) : null}
            {v.semRegioesAgenda ? (
              <div style={css('padding:28px 16px;text-align:center;color:#8a8a9e;font-size:12px;font-weight:600')}>Nenhuma área disponível</div>
            ) : null}
            {v.semRegioesRaio ? (
              <div style={css('padding:28px 16px;text-align:center;color:#8a8a9e;font-size:12px;font-weight:600')}>Nenhuma área com OS neste raio</div>
            ) : null}
            {v.mostrarRegioesAgenda ? (
              (v.regioes || []).map((reg, ri) => (
                <div key={ri} className="oosv2-agenda-area-group" style={css('border-bottom:1px solid #f4f4f8')}>
                  <Hover
                    onClick={reg.toggle}
                    className="oosv2-agenda-area-head"
                    baseStyle="display:flex;align-items:center;gap:8px;padding:8px 11px;cursor:pointer;background:#fbfbfe"
                    hoverStyle="background:#f6f3ff"
                  >
                    <div style={css(reg.caretStyle)}>
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M4.2 2.6L8 6l-3.8 3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                    </div>
                    <div className="oosv2-agenda-area-name">{reg.nome}</div>
                    {reg.coordenador ? (
                      <div style={css('font-size:10.5px;color:#a0a0b2;font-weight:600')}>coord. {reg.coordenador}</div>
                    ) : null}
                  </Hover>
                  {reg.aberto ? (
                    (reg.tecnicos || []).map((tec, ti) => (
                      <div key={ti} onClick={tec.pick} className={tec.className} style={css(tec.style)}>
                        <div style={css('display:flex;align-items:center;gap:8px;min-width:0;flex:1')}>
                          {tec.cor ? (
                            <span className="oosv2-agenda-tech-dot" style={{ background: tec.cor }} aria-hidden="true"></span>
                          ) : null}
                          <div style={css('font-size:12px;font-weight:600')}>{tec.nome}</div>
                          {tec.nearbyQtd ? (
                            <span className="oosv2-agenda-nearby-n">{tec.nearbyQtd} {tec.nearbyQtd === 1 ? 'ordem' : 'ordens'}</span>
                          ) : null}
                        </div>
                        <div style={css('display:flex;align-items:center;gap:6px')}>
                          {tec.carga ? (
                            <div style={css('font-size:10.5px;color:#8a8a9e')}>{tec.carga}</div>
                          ) : null}
                          {tec.selected && v.routeHelpAgendaMirrorEnabled ? (
                            <button type="button" className={'oosv2-agenda-ver-btn-inline' + (v.routeHelpAgendaMirrorOpen ? ' is-on' : '')} onClick={(e) => { e.stopPropagation(); v.toggleRouteHelpAgendaMirror(); }}>Ver agenda</button>
                          ) : null}
                        </div>
                      </div>
                    ))
                  ) : null}
                </div>
              ))
            ) : null}
          </div>
          </div>
          {v.routeHelpOpen ? (
            <OpenOsV2AgendaRouteMap
              km={v.routeHelpKm}
              onKmChange={v.setRouteHelpKm}
              origin={v.routeHelpOrigin}
              originAddress={v.routeHelpOriginAddress}
              points={v.routeHelpMapPoints}
              colorMap={v.routeHelpColorMap}
              loading={v.routeHelpLoading}
              error={v.routeHelpError}
              dayLabel={v.routeHelpDayLabel}
              monitoredTechs={v.routeHelpMonitoredTechs}
            />
          ) : null}
          {v.routeHelpOpen && v.routeHelpAgendaMirrorOpen ? (
            <OpenOsV2AgendaTechMirror
              technicianName={v.routeHelpAgendaMirrorTechnicianName}
              quandoDraft={v.routeHelpAgendaMirrorQuandoDraft}
              technicianObj={v.tecnicoDraftObj}
              points={v.routeHelpMirrorPoints}
            />
          ) : null}
        </div>
        <div className="oosv2-agenda-foot" style={css('display:flex;align-items:center;gap:9px;padding:10px 15px 12px;flex:0 0 auto')}>
          <div style={css('font-size:11.5px;color:#8a8a9e;flex:1')}>{v.agendaDraftResumo}</div>
          <button type="button" className="oosv2-agenda-cancel-btn" onClick={v.fecharAgenda} disabled={v.agendaConfirming} style={css('height:33px;padding:0 13px;border-radius:9px;border:1px solid #e6e6ef;background:#fff;color:#5c5c70;font-size:12px;font-weight:700;cursor:pointer')}>Cancelar</button>
          <button type="button" className={v.confirmarAgendaClass} onClick={v.confirmarAgenda} disabled={v.agendaConfirming} style={css(v.confirmarAgendaStyle)}>{v.agendaConfirming ? 'Encaminhando…' : 'Confirmar programação'}</button>
        </div>
      </div>
    </div>
  );
}

function HistTip({ text, children, wrap = false, onlyWhenTruncated = false }) {
  if (!text) return children;
  return (
    <GoHoverTooltip
      text={text}
      zIndex={100070}
      wrap={wrap}
      onlyWhenTruncated={onlyWhenTruncated}
      className="go-hover-tip-wrap--contents"
    >
      {children}
    </GoHoverTooltip>
  );
}

function cycleNo(c) {
  const n = Number(c.cycle);
  if (n > 0) return n;
  if (c.label === 'Atendimento original') return 1;
  const m = /Retorno\s+(\d+)/.exec(String(c.label || ''));
  return m ? Number(m[1]) + 1 : 0;
}

function HistCiclos({ ciclos, returnCount, numeroOS, printBase }) {
  const current = (Number(returnCount) || 0) + 1;
  const [byCycle, setByCycle] = useState(null);
  useEffect(() => {
    const os = String(numeroOS || '').trim();
    if (!os) return undefined;
    let alive = true;
    fetchOsGoAtendimento(os).then((data) => {
      if (!alive) return;
      const map = new Map();
      (data.ciclos || []).forEach((c) => map.set(Number(c.cycle), c));
      setByCycle(map);
    });
    return () => { alive = false; };
  }, [numeroOS]);
  return (
    <div className="oosv2-ciclos">
      {ciclos.map((c) => {
        const n = cycleNo(c);
        const slice = byCycle ? byCycle.get(n) : null;
        return (
          <article key={n || c.label} className="oosv2-ciclo">
            <div className="oosv2-ciclo-head">
              <span className="oosv2-ciclo-chip">Ciclo {n || '—'}</span>
              {n === 1 ? <span className="oosv2-ciclo-tag">Atendimento original</span> : null}
              {n === current ? <span className="oosv2-ciclo-tag">Atual</span> : null}
            </div>
            <div className="oosv2-ciclo-meta">
              <span><em>Condição</em> {c.condicao}</span>
              <span><em>Técnico</em> {c.tecnico}</span>
              <span><em>Fechamento</em> {c.fechamento}</span>
              <span><em>A4 P&B</em> {c.contadorPB}</span>
              {c.pecas ? <span><em>Peças</em> trocadas</span> : null}
            </div>
            {c.laudo ? (
              <HistTip text={c.laudo} wrap onlyWhenTruncated>
                <p className="oosv2-ciclo-laudo">{c.laudo}</p>
              </HistTip>
            ) : null}
            {slice ? (
              <OpenOsV2HistOsGoMedia
                numeroOS={numeroOS}
                preset={slice}
                embedded
                printRow={{
                  os: numeroOS,
                  tipo: printBase?.tipo,
                  cliente: printBase?.cliente,
                  equipamento: printBase?.equipamento,
                  serie: printBase?.serie,
                  patrimonio: printBase?.patrimonio,
                  defeito: printBase?.defeito,
                  motivo: printBase?.motivo,
                  abertura: printBase?.abertura,
                  condicaoFinal: c.condicao,
                  laudo: c.laudo,
                  tecnico: c.tecnico,
                  fimAtendimento: c.fechamento,
                }}
              />
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

export default function OpenOsV2View({ v, onClose }) {
  const cutover = useCutoverActive();
  const colLabel = (label) => (cutover && label === 'Defeito' ? 'Serviços' : label);
  const obsWrapRef = useRef(null);
  const histDialogRef = useRef(null);
  if (v.agendaOnly) {
    return (
      <div className="oosv2-page is-stage-high">
        <div>
          <OpenOsV2AgendaDialog v={v} />
        </div>
      </div>
    );
  }
  return (
    <div
      className={
        'oosv2-page' +
        (v.historyOnly ? ' oosv2-page--history-only is-stage-high' : '') +
        (!v.historyOnly && (v.isEquip || v.isDefeito || v.isFim || v.isDone) ? ' is-stage-high' : '')
      }
    >
      <div className="oosv2-shell">

        <div className="oosv2-card">
          <div className="oosv2-flow-header">
            <div className="oosv2-flow-header-top">
              <div className="oosv2-flow-brand">
                <div className="oosv2-flow-context">{v.contextoTopo}</div>
              </div>
              <div className="oosv2-flow-actions">
                {v.isDone ? null : (
                  <button type="button" className="oosv2-flow-discard" onClick={v.reiniciar}>Descartar</button>
                )}
                <button type="button" className="oosv2-flow-close" onClick={onClose} aria-label="Fechar">×</button>
              </div>
            </div>
            <div className="oosv2-flow-header-steps">
              <div className="oosv2-flow-steps">
                {(v.trilha || []).map((et, i) => (
                  <div key={i} className={et.stepClass} onClick={et.go}>
                    <div className="oosv2-flow-step-dot">
                      {et.done ? (
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.3l2.4 2.4 4.6-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                      ) : null}
                      {et.pending ? et.num : null}
                    </div>
                    <div className="oosv2-flow-step-copy">
                      <div className="oosv2-flow-step-label">{et.label}</div>
                      <div className="oosv2-flow-step-hint">{et.hint}</div>
                    </div>
                    {et.editavel ? (
                      <div className="oosv2-flow-step-edit">editar</div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            <div className="oosv2-flow-progress" aria-hidden="true"></div>
          </div>

          <div className="oosv2-flow-body">

            {v.isEquip ? (
              <div className="oosv2-equip">
                <div className="oosv2-equip-head">
                  <div className="oosv2-equip-head-title">Identificação do equipamento</div>
                  <div className="oosv2-equip-head-sub">Origem, modo de abertura e busca</div>
                </div>

                <div className="oosv2-equip-grid">
                  <div className="oosv2-equip-left">
                    <div className="oosv2-equip-kicker">Origem do equipamento</div>
                    <div className="oosv2-choice-row">
                      {(v.origens || []).map((o, i) => (
                        <div key={i} onClick={o.pick} className={o.choiceClass}>
                          <div className="oosv2-choice-head">
                            <div className="oosv2-choice-label">{o.label}</div>
                            {o.kbd ? (
                              <span className="oosv2-choice-kbd-wrap">
                                <kbd className="oosv2-choice-kbd">{o.kbd}</kbd>
                              </span>
                            ) : null}
                          </div>
                          <div className="oosv2-choice-desc">{o.desc}</div>
                        </div>
                      ))}
                    </div>

                    {v.temOrigem ? (
                      <div className="oosv2-equip-modo" style={css('animation:popIn .18s ease both')}>
                        <div className="oosv2-equip-kicker">Modo de abertura</div>
                        <div className="oosv2-choice-row">
                          {(v.modos || []).map((m, i) => (
                            <div key={i} onClick={m.pick} className={m.choiceClass}>
                              <div className="oosv2-choice-row-label">
                                <div className={m.dotClass}></div>
                                <div className="oosv2-choice-label">{m.label}</div>
                                {m.kbd ? (
                                  <span className="oosv2-choice-kbd-wrap">
                                    <kbd className="oosv2-choice-kbd">{m.kbd}</kbd>
                                  </span>
                                ) : null}
                              </div>
                              <div className="oosv2-choice-desc">{m.desc}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {v.temModo ? (
                    <div className="oosv2-equip-right" style={css('animation:popIn .18s ease both')}>
                      <div className="oosv2-equip-search-head">
                        <div className="oosv2-equip-kicker">{v.buscaTitulo}</div>
                        <div className="oosv2-equip-search-hint">{v.buscaDica}</div>
                      </div>
                      <div className="oosv2-equip-search-field">
                        <div className="oosv2-equip-search-icon">
                          <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.6" stroke="currentColor" strokeWidth="1.6"></circle><path d="M10.6 10.6L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"></path></svg>
                        </div>
                        <input className="oosv2-equip-search-input" type="text" value={v.query} onChange={v.setQuery} placeholder={v.buscaPlaceholder} />
                      </div>

                      {v.mostrarResultados ? (
                        <div className="oosv2-search-results oosv2-equip-search-results">
                          {(v.resultados || []).map((r, i) => (
                            <div key={i} className={r.rowClass || "oosv2-search-result-row"} onClick={r.pick}>
                              <div className="oosv2-search-result-copy">
                                <div className="oosv2-search-result-title">{r.titulo}</div>
                                <div className="oosv2-search-result-sub">{r.sub}</div>
                              </div>
                              {r.marcado ? (
                                <div className="oosv2-search-result-check" aria-hidden="true">
                                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.3l2.4 2.4 4.6-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                                </div>
                              ) : null}
                              <div className="oosv2-search-result-tag">{r.tag}</div>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {v.semResultados ? (
                        <div className="oosv2-equip-empty">Nenhum registro para “{v.query}”.</div>
                      ) : null}

                      {v.isEstoqueMultipla && (v.estoqueSelecionados || []).length > 0 ? (
                        <div className="oosv2-estoque-sel-panel">
                          <div className="oosv2-estoque-sel-head">
                            <div className="oosv2-estoque-sel-title">Selecionados</div>
                            <div className="oosv2-estoque-sel-count">{v.totalSelecionado}</div>
                          </div>
                          <div className="oosv2-estoque-sel-list">
                            {(v.estoqueSelecionados || []).map((item) => (
                              <div key={item.serie} className="oosv2-estoque-sel-item">
                                <div className="oosv2-estoque-sel-copy">
                                  <div className="oosv2-estoque-sel-item-title">{item.titulo}</div>
                                  <div className="oosv2-estoque-sel-item-sub">{item.sub}</div>
                                </div>
                                <button type="button" className="oosv2-estoque-sel-remove" onClick={item.remove} aria-label={"Remover " + item.titulo}>×</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {v.isEstoqueMultipla ? (
                        <div className="oosv2-equip-estoque-foot">
                          <div className="oosv2-equip-estoque-foot-hint">Um único formulário será aplicado a todos os equipamentos selecionados.</div>
                          <div className="oosv2-equip-estoque-foot-actions">
                            {v.podeAvancarEstoqueMulti ? (
                              <button
                                type="button"
                                className={'oosv2-flow-continue-btn' + (v.guardChecking ? ' is-wait' : '')}
                                disabled={!!v.guardChecking}
                                onClick={v.avancar}
                              >{v.guardChecking ? "Verificando…" : ("Continuar com " + v.totalSelecionado + " equipamento(s)")}</button>
                            ) : null}
                            {v.semSelecaoEstoqueMulti ? (
                              <button type="button" className="oosv2-flow-continue-btn is-disabled" disabled>Selecione ao menos um equipamento</button>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {v.isSelecao ? (
              <>
                <div className="oosv2-selecao-wrap">
                  <div className="oosv2-selecao-toolbar">
                    <div>
                      <div className="oosv2-selecao-title">Selecionar equipamentos</div>
                      <div className="oosv2-selecao-subtitle">{v.clienteSelecionadoNome}</div>
                    </div>
                    <div className="oosv2-selecao-toolbar-spacer"></div>
                    <input
                      type="text"
                      className="oosv2-selecao-filtro"
                      value={v.filtro}
                      onChange={v.setFiltro}
                      placeholder="Filtrar local, modelo ou série…"
                    />
                    <div className="oosv2-selecao-count">{v.totalSelecionado} selecionado(s)</div>
                  </div>

                  <div className="oosv2-selecao-tree">
                    {(v.arvore || []).map((local, li) => (
                      <div key={li} className="oosv2-selecao-local-group">
                        <div className="oosv2-selecao-local-head" onClick={local.toggle}>
                          <div className={local.boxClass} onClick={local.check}>
                            {local.marcado ? (
                              <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.3l2.4 2.4 4.6-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                            ) : null}
                          </div>
                          <div className={local.caretClass}>
                            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M4.2 2.6L8 6l-3.8 3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                          </div>
                          <SiteNameWithAddress nome={local.nome} endereco={local.endereco} />
                          <div className={local.badgeClass}>{local.selecionadosTexto}</div>
                          <div className="oosv2-selecao-meta">{local.contagem}</div>
                        </div>
                        {local.aberto ? (
                          (local.modelos || []).map((mod, mi) => (
                            <div key={mi} className="oosv2-selecao-modelo-group">
                              <div className="oosv2-selecao-modelo-head" onClick={mod.toggle}>
                                <div className={mod.boxClass} onClick={mod.check}>
                                  {mod.marcado ? (
                                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.3l2.4 2.4 4.6-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                                  ) : null}
                                </div>
                                <div className={mod.caretClass}>
                                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M4.2 2.6L8 6l-3.8 3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                                </div>
                                <div className="oosv2-selecao-modelo-name">{mod.nome}</div>
                                <div className="oosv2-selecao-meta">{mod.contagem}</div>
                              </div>
                              {mod.aberto ? (
                                (mod.series || []).map((sr, si) => (
                                  <div key={si} className={sr.rowClass} onClick={sr.check}>
                                    <div className={sr.boxClass}>
                                      {sr.marcado ? (
                                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.3l2.4 2.4 4.6-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                                      ) : null}
                                    </div>
                                    <div className="oosv2-selecao-serie-field">Série <span className="oosv2-selecao-serie-val">{sr.serie}</span></div>
                                    <div className="oosv2-selecao-serie-field">Patrimônio <span className="oosv2-selecao-serie-val">{sr.patrimonio}</span></div>
                                  </div>
                                ))
                              ) : null}
                            </div>
                          ))
                        ) : null}
                      </div>
                    ))}
                    {v.arvoreVazia ? (
                      <div className="oosv2-selecao-empty">Nada encontrado para “{v.filtro}”.</div>
                    ) : null}
                  </div>
                </div>

                <div className="oosv2-selecao-foot">
                  <button type="button" className="oosv2-flow-back-btn" onClick={v.voltar}>Voltar</button>
                  <div className="oosv2-selecao-foot-hint">Um único formulário será aplicado a todos os equipamentos selecionados.</div>
                  <div className="oosv2-selecao-toolbar-spacer"></div>
                  {v.podeAvancarMultipla ? (
                    <button
                      type="button"
                      className={'oosv2-flow-continue-btn' + (v.guardChecking ? ' is-wait' : '')}
                      disabled={!!v.guardChecking}
                      onClick={v.avancar}
                    >{v.guardChecking ? "Verificando…" : ("Continuar com " + v.totalSelecionado + " equipamento(s)")}</button>
                  ) : null}
                  {v.semSelecao ? (
                    <button type="button" className="oosv2-flow-continue-btn is-disabled" disabled>Selecione ao menos um equipamento</button>
                  ) : null}
                </div>
              </>
            ) : null}

            {v.isConf ? (
              <>
                <div style={css('animation:popIn .18s ease both')}>
                  <div style={css('display:flex;align-items:baseline;gap:8px;margin-bottom:12px')}>
                    <div style={css('font-size:13px;font-weight:700;letter-spacing:-0.01em')}>Confirmação de informações</div>
                    <div style={css('font-size:11px;color:#8a8a9e')}>Dados vindos do cadastro. Ajuste o que estiver incorreto.</div>
                  </div>

                  {v.isUnica ? (
                    <div style={css('display:grid;grid-template-columns:1.4fr 1fr;gap:10px 18px;padding-bottom:11px;border-bottom:1px solid var(--border-subtle, #f2f2f7)')}>
                      <div>
                        <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Equipamento</div>
                        <div style={css('font-size:13px;font-weight:600;margin-top:2px')}>{v.equip.modelo} <span style={css('color:#a0a0b2;font-weight:500')}>({v.equip.instalado})</span></div>
                      </div>
                      <div>
                        <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Série / Patrimônio</div>
                        <div style={css("font-size:13px;font-weight:600;margin-top:2px;font-family:'JetBrains Mono',monospace")}>{v.equip.serie} • {v.equip.patrimonio}</div>
                      </div>
                    </div>
                  ) : null}

                  {v.isMultipla ? (
                    <div className="oosv2-multi-conf-bar">
                      <div className="oosv2-multi-conf-count">{v.totalSelecionado}</div>
                      <div className="oosv2-multi-conf-copy">
                        <div className="oosv2-multi-conf-title">Equipamentos nesta abertura</div>
                        <div className="oosv2-multi-conf-sub">{v.resumoLocais}</div>
                      </div>
                      <button type="button" className="oosv2-done-action-btn oosv2-done-action-btn--accent oosv2-done-action-btn--sm" onClick={v.abrirLista}>Ver equipamentos</button>
                      <button type="button" className="oosv2-done-action-btn oosv2-done-action-btn--sm" onClick={v.voltar}>Editar seleção</button>
                    </div>
                  ) : null}

                  <div style={css('padding:11px 0;border-bottom:1px solid var(--border-subtle, #f2f2f7)')}>
                    <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Cliente</div>
                    <div style={css('display:flex;align-items:center;gap:8px;margin-top:2px;flex-wrap:wrap')}>
                      <div style={css('font-size:13px;font-weight:600')}>{v.equip.cliente}</div>
                      {v.contratoVencido ? (
                        <span className="oosv2-badge-expired">Contrato vencido</span>
                      ) : null}
                    </div>
                  </div>

                  {v.mostrarEnderecoConf ? (
                    <div style={css('padding:11px 0;border-bottom:1px solid var(--border-subtle, #f2f2f7)')}>
                      <div style={css('display:flex;align-items:center;gap:10px')}>
                        <div style={css('flex:1;min-width:0')}>
                          <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Endereço de instalação</div>
                          <div className="oosv2-endereco-line">
                            <div className="oosv2-endereco-text">{v.enderecoAtual}</div>
                            {v.enderecoCorrigido ? (
                              <span className="oosv2-badge-ok-wrap">
                                <span className="oosv2-badge-ok">✓ Endereço corrigido</span>
                                <span className="oosv2-badge-ok-tip">{v.enderecoOriginal}</span>
                              </span>
                            ) : null}
                          </div>
                          <div style={css('display:flex;align-items:center;gap:6px;margin-top:4px')}>
                            <div style={css('font-size:11px;color:#8a8a9e')}>Localização interna:</div>
                            <div style={css('font-size:11px;font-weight:700;color:#6d3ef5')}>{v.localInternoAtual}</div>
                            <Hover
                              as="button"
                              type="button"
                              onClick={v.abrirLocalInterno}
                              baseStyle="border:none;background:none;color:#8a8a9e;font-size:11px;font-weight:600;cursor:pointer;text-decoration:underline"
                              hoverStyle="color:#6d3ef5"
                            >editar</Hover>
                          </div>
                        </div>
                        <button type="button" className={v.btnEnderecoClass} onClick={v.toggleEndereco}>Endereço incorreto</button>
                      </div>

                      {v.editandoLocalInterno ? (
                        <div style={css('margin-top:9px;display:flex;gap:7px;align-items:center;animation:popIn .16s ease both')}>
                          <input type="text" className="oosv2-local-interno-input" value={v.localInternoDraft} onChange={v.setLocalInterno} placeholder="Ex.: POSTO DE SAÚDE GRANJA — sala 2" style={css('flex:1;height:32px;border:1px solid var(--border-subtle, #d9d4f7);border-radius:8px;padding:0 10px;font-size:12.5px;outline:none;background:var(--input-bg, #fff)')} />
                          <button type="button" className="oosv2-local-cancel" onClick={v.cancelarLocalInterno}>Cancelar</button>
                          <button type="button" className="oosv2-local-save" onClick={v.salvarLocalInterno}>Salvar</button>
                        </div>
                      ) : null}

                      {v.corrigindoEndereco ? (
                        <div className="oosv2-addr-edit" style={css('animation:popIn .16s ease both')}>
                          <div className="oosv2-equip-kicker">Buscar endereço correto</div>
                          <div className="oosv2-addr-field">
                            <input className="oosv2-addr-input" type="text" value={v.enderecoQuery} onChange={v.setEnderecoQuery} placeholder="Digite rua, número ou CEP…" />
                            {v.buscandoEndereco ? (
                              <div className="oosv2-addr-loading">Buscando…</div>
                            ) : null}
                          </div>
                          {v.temSugestoesEndereco ? (
                            <div className="oosv2-addr-list">
                              {(v.sugestoesEndereco || []).map((sug, i) => (
                                <div key={i} className="oosv2-addr-item" onClick={sug.pick}>{sug.texto}</div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {v.mostrarUltimoConf ? (
                    <div style={css('padding:10px 0;border-bottom:1px solid var(--border-subtle, #f2f2f7)')}>
                      <div style={css('display:flex;align-items:center;gap:8px;flex-wrap:wrap')}>
                        <div
                          className={"oosv2-soft-chip" + (v.ultimoAberto ? " is-on" : "")}
                          onClick={v.toggleUltimo}
                        >
                          <div style={css(v.caretUltimoStyle)}>
                            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M4.2 2.6L8 6l-3.8 3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                          </div>
                          <span style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Último atendimento</span>
                          <span style={css('font-size:11.5px;font-weight:700;color:#6d3ef5')}>OS {v.ultimo.os} · {v.ultimo.data}</span>
                        </div>
                        <div className="oosv2-soft-chip" onClick={v.abrirHistorico}>
                          <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 7a5 5 0 105-5" stroke="#6d3ef5" strokeWidth="1.6" strokeLinecap="round"></path><path d="M7 4.2V7l1.9 1.2" stroke="#6d3ef5" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                          <span style={css('font-size:11.5px;font-weight:700;color:#8a8a9e')}>Histórico do equipamento</span>
                          <span className="oosv2-hist-os-badge">{v.historicoTotalOS} OS</span>
                        </div>
                      </div>

                      {v.ultimoAberto ? (
                        <div className="oosv2-ultimo-panel" style={css('animation:popIn .16s ease both')}>
                          <div style={css('display:grid;grid-template-columns:repeat(3,minmax(0,1fr)) auto;gap:10px;align-items:center;padding:10px 12px;background:var(--bg-surface-alt, #fbfbfe);border-bottom:1px solid var(--border-subtle, #f2f2f7)')}>
                            <div>
                              <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Solicitante</div>
                              <div style={css('font-size:12.5px;font-weight:600')}>{v.ultimo.solicitante}</div>
                            </div>
                            <div>
                              <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Tipo</div>
                              <div style={css('font-size:12.5px;font-weight:600')}>{v.ultimo.tipo}</div>
                            </div>
                            <div>
                              <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Técnico</div>
                              <div style={css('font-size:12.5px;font-weight:600')}>{v.ultimo.tecnico}</div>
                            </div>
                            <Hover
                              as="button"
                              type="button"
                              onClick={v.toggleLaudo}
                              baseStyle="height:28px;padding:0 11px;border-radius:8px;border:1px solid var(--border-subtle, #d9d4f7);background:var(--bg-surface-raised, #fff);color:#6d3ef5;font-size:11px;font-weight:700;cursor:pointer"
                              hoverStyle="background:var(--bg-surface-alt, #f6f3ff)"
                            >{v.laudoLabel}</Hover>
                          </div>
                          <div className="oosv2-ultimo-defeito-row">
                            <div className="oosv2-ultimo-kicker">Defeito</div>
                            <div className="oosv2-ultimo-defeito-copy">
                              {v.temUltimoAlerta ? (
                                <span className="oosv2-alert-code oosv2-ultimo-alert" style={css(v.ultimoAlertaStyle)}>{v.ultimoAlerta}</span>
                              ) : null}
                              <div className="oosv2-ultimo-defeito-text">{v.ultimo.defeito}</div>
                              <div className="oosv2-ultimo-obs-wrap" ref={obsWrapRef}>
                                <button type="button" className={v.obsTipBtnClass} onClick={v.toggleDefeitoDetalhe} aria-label="Ver observações e motivo">
                                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.5"></circle><path d="M8 7.2v3.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path><circle cx="8" cy="5.1" r="0.7" fill="currentColor"></circle></svg>
                                </button>
                                <span className="oosv2-ultimo-obs-hover-tip">Ver observações e motivo</span>
                                <UltimoObsTipPortal
                                  open={!!v.defeitoDetalheAberto}
                                  anchorRef={obsWrapRef}
                                  observacao={v.ultimo.observacao}
                                  motivo={v.ultimo.motivo}
                                />
                              </div>
                            </div>
                          </div>
                          {v.laudoAberto ? (
                            <div style={css('border-top:1px solid var(--border-subtle, #f2f2f7);padding:11px 12px;background:var(--bg-surface-alt, #fdfdff);animation:popIn .16s ease both')}>
                              <div style={css('display:flex;align-items:center;gap:8px;margin-bottom:8px')}>
                                <div style={css('font-size:12px;font-weight:700')}>Laudo de atendimento — OS #{v.ultimo.os}</div>
                                <div style={css('font-size:11px;color:#8a8a9e')}>{v.ultimo.laudoData}</div>
                              </div>
                              <div style={css('display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:8px')}>
                                <div>
                                  <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Condição de intervenção</div>
                                  <div style={css('font-size:12.5px;font-weight:600')}>{v.ultimo.condicao}</div>
                                </div>
                                <div>
                                  <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Técnico</div>
                                  <div style={css('font-size:12.5px;font-weight:600')}>{v.ultimo.tecnico}</div>
                                </div>
                              </div>
                              <div style={css('font-size:12px;line-height:1.5;color:var(--text-body, #4a4a5c);border:1px solid var(--border-subtle, #ebebf2);border-radius:9px;padding:8px 10px;background:var(--bg-surface-raised, #fff)')}>{v.ultimo.laudoTexto}</div>
                              <div style={css('display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:1px;background:var(--border-subtle, #f2f2f7);border:1px solid var(--border-subtle, #f2f2f7);border-radius:9px;overflow:hidden;margin-top:8px')}>
                                {(v.ultimo.contadores || []).map((c, i) => (
                                  <div key={i} style={css('background:var(--bg-surface-raised, #fff);padding:7px 9px')}>
                                    <div style={css('font-size:10px;color:#8a8a9e;font-weight:600')}>{c.label}</div>
                                    <div style={css("font-size:12.5px;font-weight:700;font-family:'JetBrains Mono',monospace")}>{c.valor}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div style={css('padding-top:12px')}>
                    <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700;margin-bottom:7px')}>Quem está solicitando</div>
                    <div className="oosv2-solicitante-grid">
                      <div>
                        <div style={css('display:flex;align-items:center;gap:5px;margin-bottom:4px')}>
                          <div style={css('font-size:11px;font-weight:700;color:#5c5c70')}>Solicitante</div>
                          <div style={css(v.marcaSolicitante)}></div>
                        </div>
                        <div className="oosv2-field-wrap">
                          <input type="text" className={v.solicitanteInputClass} value={v.solicitante} onChange={v.setSolicitante} placeholder="Nome e sobrenome" style={css(v.inputSolicitante)} />
                          <button type="button" className={v.tbSolicitante.className} onClick={v.tbSolicitante.pick} aria-label={v.tbSolicitante.tip}>
                            <span className="oosv2-tb02112-tip">{v.tbSolicitante.tip}</span>
                            {v.tbSolicitante.showCheck ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="2" /><path d="m9 12 2 2 4-4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            ) : null}
                            {v.tbSolicitante.showBook ? (
                              <svg className="oosv2-tb02112-ico" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h-6a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2h8" /><path d="M18 4v17" /><path d="M15 18l3 3 3-3" /></svg>
                            ) : null}
                          </button>
                        </div>
                      </div>
                      <div>
                        <div style={css('display:flex;align-items:center;gap:5px;margin-bottom:4px')}>
                          <div style={css('font-size:11px;font-weight:700;color:#5c5c70')}>Contato</div>
                          <div style={css(v.marcaContato)}></div>
                        </div>
                        <div className="oosv2-contato-row">
                          <div className="oosv2-field-wrap">
                            <input type="text" className={v.contatoInputClass} value={v.contato} onChange={v.setContato} placeholder="(XX)XXXXX-XXXX" maxLength="14" style={css(v.inputContato)} />
                            <button type="button" className={v.tbContato.className} onClick={v.tbContato.pick} aria-label={v.tbContato.tip}>
                              <span className="oosv2-tb02112-tip">{v.tbContato.tip}</span>
                              {v.tbContato.showCheck ? (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="2" /><path d="m9 12 2 2 4-4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              ) : null}
                              {v.tbContato.showBook ? (
                                <svg className="oosv2-tb02112-ico" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h-6a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2h8" /><path d="M18 4v17" /><path d="M15 18l3 3 3-3" /></svg>
                              ) : null}
                            </button>
                          </div>
                          <div className="oosv2-fone-types">
                            {(v.tiposContato || []).map((t, i) => (
                              <div key={i} onClick={t.pick} className={t.className} style={css(t.style)}>{t.label}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div style={css('display:flex;align-items:center;gap:5px;margin-bottom:4px')}>
                          <div style={css('font-size:11px;font-weight:700;color:#5c5c70')}>E-mail</div>
                          <div style={css(v.emailHintStyle)}>{v.emailHint}</div>
                        </div>
                        <div className={v.emailBoxClass}>
                          <div className="oosv2-email-chips">
                            {(v.emailChips || []).map((em, i) => (
                              <span key={i} className="oosv2-email-chip">
                                <span className="oosv2-email-chip-text">{em.addr}</span>
                                <button type="button" className="oosv2-email-chip-x" onClick={em.remove} aria-label="Remover e-mail">×</button>
                              </span>
                            ))}
                            <input type="text" className="oosv2-email-input" value={v.emailDraft} onChange={v.setEmail} onKeyDown={v.onEmailKey} onBlur={v.onEmailBlur} placeholder="email@exemplo.com" />
                          </div>
                          <button type="button" className={v.tbEmail.className} onClick={v.tbEmail.pick} aria-label={v.tbEmail.tip}>
                            <span className="oosv2-tb02112-tip">{v.tbEmail.tip}</span>
                            {v.tbEmail.showCheck ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="2" /><path d="m9 12 2 2 4-4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            ) : null}
                            {v.tbEmail.showBook ? (
                              <svg className="oosv2-tb02112-ico" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h-6a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2h8" /><path d="M18 4v17" /><path d="M15 18l3 3 3-3" /></svg>
                            ) : null}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={css('display:flex;align-items:center;gap:10px')}>
                  <button type="button" className="oosv2-flow-back-btn" onClick={v.voltar}>Voltar</button>
                  <div style={css('flex:1')}></div>
                  {v.form1Ok ? (
                    <Hover
                      as="button"
                      type="button"
                      onClick={v.avancar}
                      baseStyle="height:36px;padding:0 16px;border-radius:9px;border:none;background:#6d3ef5;color:#fff;font-size:12.5px;font-weight:700;cursor:pointer;box-shadow:0 1px 2px rgba(109,62,245,.4)"
                      hoverStyle="background:#5a2ee0"
                    >Continuar para o defeito</Hover>
                  ) : null}
                  {v.form1Pendente ? (
                    <div style={css('display:flex;align-items:center;gap:10px')}>
                      <div style={css('font-size:11.5px;color:#8a8a9e;font-weight:600')}>{v.form1Faltando}</div>
                      <button type="button" disabled style={css('height:36px;padding:0 16px;border-radius:9px;border:1px solid var(--border-subtle, #ebebf2);background:var(--bg-surface-alt, #f5f5f9);color:var(--text-muted, #b4b4c2);font-size:12.5px;font-weight:700;cursor:not-allowed')}>Continuar para o defeito</button>
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}

            {v.isDefeito ? (
              <>
                <div style={css('animation:popIn .18s ease both')}>
                  <div style={css('display:flex;align-items:baseline;gap:8px;margin-bottom:12px')}>
                    <div style={css('font-size:13px;font-weight:700;letter-spacing:-0.01em')}>Defeito relatado</div>
                    <div style={css('font-size:11px;color:#8a8a9e')}>Um defeito por OS. A observação abre após a escolha.</div>
                  </div>

                  <div style={css('max-width:560px')}>
                    <div className="oosv2-defeito-label-row">
                      <div className="oosv2-defeito-label">Defeito do cadastro</div>
                      <p className="oosv2-defeito-hint">(Digite os três primeiros caracteres do defeito).</p>
                    </div>
                    <input type="text" className="oosv2-defeito-input" value={v.defeitoQuery} onChange={v.setDefeitoQuery} placeholder="Comece a digitar: atola, barulho, mancha…" style={css('width:100%;height:34px;border:1px solid var(--border-subtle, #d9d4f7);border-radius:9px;padding:0 10px;font-size:13px;outline:none;background:var(--input-bg, #fff)')} />
                    {v.mostrarDefeitos ? (
                      <div className="oosv2-defeito-results">
                        {(v.defeitosFiltrados || []).map((d, i) => (
                          <div key={i} className="oosv2-defeito-result-row" onClick={d.pick}>
                            <span className="oosv2-defeito-result-name">{d.nome}</span>
                            {(d.badges && d.badges.length) ? (
                              <span className="oosv2-defeito-tipo-badges">
                                {d.badges.map((b) => (
                                  <DefeitoTipoOsBadge key={b.sigla} sigla={b.sigla} label={b.label} />
                                ))}
                              </span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  {v.temDefeito ? (
                    <div style={css('margin-top:13px;padding-top:12px;border-top:1px dashed var(--border-subtle, #ececf3);animation:popIn .18s ease both')}>
                      <div style={css('display:flex;align-items:center;gap:8px;margin-bottom:7px')}>
                        <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Defeito selecionado</div>
                        <div className="oosv2-defeito-chip">
                          <span className="oosv2-defeito-chip-label">{v.defeito}</span>
                          <button type="button" className="oosv2-defeito-chip-clear" onClick={v.limparDefeito} aria-label="Remover defeito">×</button>
                        </div>
                      </div>
                      <div className="oosv2-obs-block">
                        <div className="oosv2-obs-counter-row">
                          <span className={v.contadorObsClass}>{v.obsContador}</span>
                        </div>
                        <div className="oosv2-obs-body">
                          <textarea
                            value={v.obs}
                            onChange={v.setObs}
                            placeholder="Descreva o que o cliente relatou (obrigatório, mín. 15 caracteres) — ou pressione espaço para a Orya te ajudar ✨"
                            className="oosv2-obs-textarea"
                            data-orya-context={v.defeito ? `Observação de abertura de OS. Defeito relatado: ${v.defeito}` : 'Observação de abertura de OS.'}
                            style={css('width:100%;min-height:86px;border:1px solid var(--border-subtle, #d9d4f7);border-radius:10px;padding:9px 10px;font-size:13px;line-height:1.5;outline:none;resize:vertical;background:var(--input-bg, #fff)')}
                          />
                          <div className="oosv2-obs-tools">
                            <button type="button" className="oosv2-obs-tool-btn" data-oosv2-obs-voice="1" aria-label="Fala para texto">
                              <span className="oosv2-obs-tool-tip">Grave em voz e tenha o texto transcrito aqui</span>
                              <svg className="oosv2-obs-tool-ico" width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="6" y="2" width="4" height="7" rx="2" stroke="currentColor" strokeWidth="1.5"></rect><path d="M4 8a4 4 0 008 0M8 12v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path></svg>
                              <span>Fala para texto</span>
                            </button>
                            <button type="button" className="oosv2-obs-tool-btn" data-oosv2-obs-ai="1" aria-label="Texto com IA">
                              <span className="oosv2-obs-tool-tip">Orya pode te ajudar a deixar o texto melhor</span>
                              <svg className="oosv2-obs-tool-ico" width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 2l1.3 3.2L12.5 6.5 9.3 7.8 8 11 6.7 7.8 3.5 6.5 6.7 5.2 8 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"></path><path d="M12.5 11.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6.6-1.4z" fill="currentColor"></path></svg>
                              <span>Texto com IA</span>
                            </button>
                          </div>
                        </div>
                      </div>
                      {v.mostrarTiposOs ? (
                        <div style={css('margin-top:12px')}>
                          <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700;margin-bottom:7px')}>Tipo de OS</div>
                          <div style={css('display:flex;flex-wrap:wrap;gap:7px')}>
                            {(v.tiposOS || []).map((t, i) => (
                              <div key={i} onClick={t.pick} className={t.className} style={css(t.style)}>{t.label}</div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {v.mostrarTiposOs ? (
                    <div style={css('margin-top:12px;animation:popIn .18s ease both')}>
                      <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700;margin-bottom:7px')}>Tipo de OS</div>
                      <div style={css('display:flex;flex-wrap:wrap;gap:7px')}>
                        {(v.tiposOS || []).map((t, i) => (
                          <div key={i} onClick={t.pick} className={t.className} style={css(t.style)}>{t.label}</div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div style={css('display:flex;align-items:center;gap:10px')}>
                  <button type="button" className="oosv2-flow-back-btn" onClick={v.voltar}>Voltar</button>
                  <div style={css('flex:1')}></div>
                  {v.defeitoOk ? (
                    <button type="button" className="oosv2-flow-continue-btn" onClick={v.avancar}>Continuar para finalizar</button>
                  ) : null}
                  {v.defeitoPendente ? (
                    <div style={css('display:flex;align-items:center;gap:10px')}>
                      <div style={css('font-size:11.5px;color:#8a8a9e;font-weight:600')}>{v.defeitoFaltando}</div>
                      <button type="button" disabled className="oosv2-flow-continue-btn is-disabled">Continuar para finalizar</button>
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}

            {v.isFim ? (
              <>
                <div style={css('animation:popIn .18s ease both')}>
                  <div style={css('display:flex;align-items:baseline;gap:8px;margin-bottom:12px')}>
                    <div style={css('font-size:13px;font-weight:700;letter-spacing:-0.01em')}>Finalizar abertura</div>
                    <div style={css('font-size:11px;color:#8a8a9e')}>Escolha o tipo de OS e, se quiser, já programe o atendimento.</div>
                  </div>

                  <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700;margin-bottom:7px')}>Tipo de OS</div>
                  {(!v.defeitoVinculoAtivo || v.mostrarTiposOs) && (v.tiposOS || []).length > 0 ? (
                  <div style={css('display:flex;flex-wrap:wrap;gap:7px')}>
                    {(v.tiposOS || []).map((t, i) => (
                      <div key={i} onClick={t.pick} className={t.className} style={css(t.style)}>{t.label}</div>
                    ))}
                  </div>
                  ) : null}

                  <div className={v.cardProgramarClass} style={css(v.cardProgramarStyle)}>
                    <div style={css('display:flex;align-items:center;gap:12px')}>
                      <div className={v.iconeProgramarClass} style={css(v.iconeProgramarStyle)}>
                        <svg width="17" height="17" viewBox="0 0 18 18" fill="none"><rect x="2.5" y="3.5" width="13" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.5"></rect><path d="M2.5 7.2h13M6 2.2v2.4M12 2.2v2.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path></svg>
                      </div>
                      <div style={css('flex:1;min-width:0')}>
                        <div style={css('font-size:13px;font-weight:700')}>Programar atendimento</div>
                        <div className={v.textoProgramarClass} style={css(v.textoProgramarStyle)}>{v.agendaResumo}</div>
                      </div>
                      <button type="button" className={v.botaoAgendaClass} onClick={v.abrirAgenda} style={css(v.botaoAgendaStyle)}>{v.agendaBotao}</button>
                    </div>
                  </div>

                  <div style={css('margin-top:10px')}>
                    <Hover
                      as="button"
                      type="button"
                      onClick={v.toggleContadores}
                      baseStyle="border:none;background:none;padding:0;color:#8a8a9e;font-size:11.5px;font-weight:600;cursor:pointer;text-decoration:underline;text-underline-offset:3px"
                      hoverStyle="color:#6d3ef5"
                    >{v.contadoresLabel}</Hover>
                    {v.contadoresAberto ? (
                      <>
                        {v.contadoresLoading ? (
                          <div style={css('margin-top:8px;font-size:11px;color:#8a8a9e;font-weight:600')}>Carregando contadores…</div>
                        ) : null}
                        {v.mostrarContadoresInputs ? (
                          <div style={css('display:flex;gap:7px;align-items:flex-end;margin-top:8px;animation:popIn .16s ease both')}>
                            <div style={css('width:120px')}>
                              <div style={css('font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:#8a8a9e;font-weight:700;margin-bottom:3px')}>P&B</div>
                              <input type="text" inputMode="numeric" className="oosv2-contador-input" value={v.contPb} onChange={v.setContPb} placeholder={v.contPbPh} style={css("width:100%;height:31px;border:1px solid #e6e6ef;border-radius:8px;padding:0 9px;font-size:12.5px;font-family:'JetBrains Mono',monospace;outline:none;background:#fff")} />
                            </div>
                            <div style={css('width:120px')}>
                              <div style={css('font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:#8a8a9e;font-weight:700;margin-bottom:3px')}>Cor</div>
                              <input type="text" inputMode="numeric" className="oosv2-contador-input" value={v.contCor} onChange={v.setContCor} placeholder={v.contCorPh} style={css("width:100%;height:31px;border:1px solid #e6e6ef;border-radius:8px;padding:0 9px;font-size:12.5px;font-family:'JetBrains Mono',monospace;outline:none;background:#fff")} />
                            </div>
                            <div style={css('width:120px')}>
                              <div style={css('font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:#8a8a9e;font-weight:700;margin-bottom:3px')}>Total</div>
                              <input type="text" inputMode="numeric" className="oosv2-contador-input" value={v.contTotal} onChange={v.setContTotal} placeholder={v.contTotalPh} style={css("width:100%;height:31px;border:1px solid #e6e6ef;border-radius:8px;padding:0 9px;font-size:12.5px;font-family:'JetBrains Mono',monospace;outline:none;background:#fff")} />
                            </div>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </div>

                  <div className="oosv2-fim-resumo" style={css('margin-top:14px;padding-top:12px;border-top:1px solid var(--border-subtle, #f2f2f7);display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px')}>
                    <div>
                      <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Equipamentos</div>
                      <div style={css('font-size:12.5px;font-weight:600')}>{v.totalSelecionado}</div>
                    </div>
                    <div>
                      <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Solicitante</div>
                      <div style={css('font-size:12.5px;font-weight:600')}>{v.solicitante}</div>
                    </div>
                    <div>
                      <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Defeito</div>
                      <div style={css('font-size:12.5px;font-weight:600')}>{v.defeito}</div>
                    </div>
                    <div>
                      <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Tipo</div>
                      <div style={css('font-size:12.5px;font-weight:600')}>{v.tipoOS}</div>
                    </div>
                  </div>
                </div>

                <div style={css('display:flex;align-items:center;gap:10px')}>
                  <button type="button" className="oosv2-flow-back-btn" onClick={v.voltar}>Voltar</button>
                  <div style={css('flex:1')}></div>
                  {v.canProgramarPreventiva ? (
                    <Hover
                      as="button"
                      type="button"
                      className="oosv2-programar-btn is-active"
                      disabled={!!v.gerandoOS}
                      onClick={v.programarPreventivas}
                      baseStyle={"height:38px;padding:0 16px;border-radius:10px;font-size:13px;font-weight:700;display:inline-flex;align-items:center;gap:7px;" + (v.gerandoOS ? "cursor:wait;opacity:.72" : "cursor:pointer")}
                      hoverStyle=""
                    >
                      <IconCalendarPlus size={16} stroke={1.7} aria-hidden />
                      {v.gerandoOS ? "Programando…" : "Programar"}
                    </Hover>
                  ) : null}
                  <Hover
                    as="button"
                    type="button"
                    className="oosv2-gerar-btn"
                    disabled={!!v.gerandoOS}
                    onClick={v.gerarOS}
                    baseStyle={"height:38px;padding:0 20px;border-radius:10px;border:none;background:#6d3ef5;color:#fff;font-size:13px;font-weight:700;box-shadow:0 2px 6px rgba(109,62,245,.35);" + (v.gerandoOS ? "cursor:wait;opacity:.72" : "cursor:pointer")}
                    hoverStyle={v.gerandoOS ? "" : "background:#5a2ee0"}
                  >
                    <IconTicket size={16} stroke={1.7} aria-hidden />
                    {v.gerandoOS ? "Gerando…" : v.gerarLabel}
                  </Hover>
                </div>
              </>
            ) : null}

            {v.isDone ? (
              <div style={css('animation:popIn .2s ease both')}>
                <div style={css('display:flex;align-items:center;gap:10px;margin-bottom:14px')}>
                  <div style={css('width:30px;height:30px;border-radius:10px;background:#e7f7ee;color:#12a45a;display:flex;align-items:center;justify-content:center')}>
                    <svg width="15" height="15" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.3l2.4 2.4 4.6-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                  </div>
                  <div>
                    <div style={css('font-size:14px;font-weight:700;letter-spacing:-0.01em')}>{v.tituloFinal}</div>
                    <div style={css('font-size:11.5px;color:#8a8a9e')}>{v.subtituloFinal}</div>
                  </div>
                  <div style={css('flex:1')}></div>
                  {!v.preventivasProgramadas ? (
                    <>
                      <button
                        type="button"
                        className="oosv2-done-action-btn oosv2-done-action-btn--accent"
                        onClick={v.imprimirTodas}
                      >{v.imprimirLabel}</button>
                      <button
                        type="button"
                        className="oosv2-done-action-btn oosv2-done-action-btn--accent"
                        onClick={v.enviarTodas}
                      >Enviar por e-mail</button>
                    </>
                  ) : null}
                </div>

                <div className={'oosv2-os-list' + ((v.osGeradas || []).length > 8 ? ' is-scroll' : '')}>
                  {(v.osGeradas || []).map((os, i) => (
                    <div key={i} className={'oosv2-os-list-item' + (v.variasOS ? ' is-multi' : '')}>
                      <div className="oosv2-os-list-num">{v.preventivasProgramadas ? 'Prog.' : `#${os.numero}`}</div>
                      <div className="oosv2-os-list-body">
                        <div className="oosv2-os-list-title">{os.modelo} · {os.serie}</div>
                        <div className="oosv2-os-list-meta">{os.linha}</div>
                      </div>
                      {v.variasOS && !v.preventivasProgramadas ? (
                        <div className="oosv2-os-list-actions">
                          <button
                            type="button"
                            className="oosv2-done-action-btn oosv2-done-action-btn--accent oosv2-done-action-btn--sm"
                            onClick={() => v.imprimirOS(os.numero)}
                          >Imprimir / PDF</button>
                          <button
                            type="button"
                            className="oosv2-done-action-btn oosv2-done-action-btn--sm"
                            onClick={() => v.enviarOS(os.numero)}
                          >E-mail</button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>

                <div style={css('margin-top:14px;display:flex;gap:8px')}>
                  <button type="button" className="oosv2-done-finalizar" onClick={onClose}>Finalizar</button>
                  <Hover
                    as="button"
                    type="button"
                    onClick={v.reiniciar}
                    baseStyle="height:36px;padding:0 15px;border-radius:9px;border:none;background:#16161d;color:#fff;font-size:12.5px;font-weight:700;cursor:pointer"
                    hoverStyle="background:#2a2a36"
                  >Abrir outra OS</Hover>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {v.listaModal ? (
        <div
          className="oosv2-lista-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) v.fecharLista();
          }}
        >
          <div className="oosv2-lista-modal">
            <div className="oosv2-lista-modal-head">
              <div className="oosv2-lista-modal-title">{v.totalSelecionado} equipamentos selecionados</div>
              <div className="oosv2-selecao-toolbar-spacer"></div>
              <button type="button" className="oosv2-flow-close oosv2-flow-close--lg" onClick={v.fecharLista} aria-label="Fechar">×</button>
            </div>
            <div className="oosv2-lista-modal-body">
              {(v.selecionados || []).map((sel, i) => (
                <div key={i} className="oosv2-lista-modal-row">
                  <div className="oosv2-lista-modal-serie">{sel.serie}</div>
                  <div className="oosv2-lista-modal-modelo">{sel.modelo}</div>
                  <div className="oosv2-lista-modal-local">{sel.local}</div>
                </div>
              ))}
            </div>
            <div className="oosv2-lista-modal-foot">
              <button type="button" className="oosv2-done-action-btn oosv2-done-action-btn--sm" onClick={v.fecharLista}>Fechar</button>
            </div>
          </div>
        </div>
      ) : null}

      <OpenOsV2AgendaDialog v={v} />

      {v.historicoAberto ? (
        <div
          className="oosv2-hist-backdrop"
          style={css('position:fixed;inset:0;background:rgba(15,15,28,.55);display:flex;align-items:center;justify-content:center;padding:22px;z-index:55')}
        >
          <div className="oosv2-hist-dialog" ref={histDialogRef}>

            <div className="oosv2-hist-header">
              <div className="oosv2-hist-header-title">
                <div style={css('font-size:14px;font-weight:700;letter-spacing:-0.01em')}>Histórico do equipamento</div>
                <div className="oosv2-hist-header-sub">
                  {v.equip.modelo} · <span className="oosv2-hist-mono">{v.equip.serie}</span> · Patrimônio <span className="oosv2-hist-mono">{v.equip.patrimonio}</span>
                </div>
              </div>
              <div className="oosv2-hist-header-right">
                <nav className="oosv2-hist-tabs">
                  {(v.historicoAbas || []).map((ab, i) => (
                    <div key={i} className={ab.className}>
                      <button type="button" onClick={ab.pick} className="oosv2-hist-tab">
                        <span>{ab.label}</span>
                        {ab.temBadge ? (
                          <span className="oosv2-hist-count">{ab.badge}</span>
                        ) : null}
                      </button>
                      {ab.mostrarPeriodo ? (
                        <div className="oosv2-hist-cols oosv2-hist-period">
                          <button type="button" className="oosv2-hist-cols-btn" onClick={v.togglePeriodo} aria-label="Período do histórico">
                            <IconCalendarCog size={14} stroke={1.7} aria-hidden />
                          </button>
                          <span className="oosv2-hist-cols-tip" role="tooltip">Período do histórico</span>
                          {v.periodoAberto ? (
                            <div className="oosv2-hist-cols-menu">
                              <div className="oosv2-hist-cols-menu-title">Período</div>
                              {(v.periodoOpcoes || []).map((op, oi) => (
                                <div key={oi} onClick={op.pick} className="oosv2-hist-cols-menu-item">
                                  <span className={op.boxClass} aria-hidden="true"></span>
                                  <div className="oosv2-hist-cols-menu-label">{op.label}</div>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {ab.mostrarColunas ? (
                        <div className="oosv2-hist-cols">
                          <button type="button" className="oosv2-hist-cols-btn" onClick={v.toggleColunas} aria-label="Personalize colunas da tabela">
                            <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><rect x="1.6" y="2.2" width="10.8" height="9.6" rx="1.8" stroke="currentColor" strokeWidth="1.4"></rect><path d="M5.6 2.2v9.6M9 2.2v9.6" stroke="currentColor" strokeWidth="1.4"></path></svg>
                          </button>
                          <span className="oosv2-hist-cols-tip" role="tooltip">Personalize colunas da tabela</span>
                          {v.colunasAberto ? (
                            <div className="oosv2-hist-cols-menu">
                              <div className="oosv2-hist-cols-menu-title">Exibir colunas</div>
                              {(v.colunasControle || []).map((cc, ci) => (
                                <div key={ci} onClick={cc.toggle} className="oosv2-hist-cols-menu-item">
                                  <span className={cc.boxClass} aria-hidden="true"></span>
                                  <div className="oosv2-hist-cols-menu-label">{colLabel(cc.label)}</div>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </nav>
              </div>
              <button type="button" className="oosv2-flow-close oosv2-hist-close" onClick={v.fecharHistorico} aria-label="Fechar histórico">×</button>
            </div>

            <div className="oosv2-hist-body" style={css('flex:1;padding:14px 16px')} onClick={v.fecharGanttTip}>

              {v.abaResumo ? (
                <>
                  <div style={css('display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:9px')}>
                    <div className="oosv2-hist-stat-card" style={css('border:1px solid #ebebf2;border-radius:13px;padding:11px 12px;background:#fdfdff')}>
                      <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Tempo instalado</div>
                      <div className="oosv2-hist-stat-value" style={css('font-size:20px;font-weight:700;letter-spacing:-0.02em;margin-top:3px')}>{v.hist.tempoInstalado}</div>
                      <div style={css('font-size:11px;color:#8a8a9e;font-weight:600')}>desde {v.hist.dataInstalacao}</div>
                    </div>
                    <div className="oosv2-hist-stat-card" style={css('border:1px solid #ebebf2;border-radius:13px;padding:11px 12px;background:#fdfdff')}>
                      <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Chamados no período</div>
                      <div style={css('display:flex;align-items:baseline;gap:6px;margin-top:3px')}>
                        <div className="oosv2-hist-stat-value" style={css('font-size:20px;font-weight:700;letter-spacing:-0.02em')}>{v.hist.totalChamados}</div>
                        <div style={css('font-size:11px;color:#8a8a9e;font-weight:600')}>{v.hist.periodo}</div>
                      </div>
                      <div style={css('display:flex;height:6px;border-radius:999px;overflow:hidden;margin-top:7px;background:#f0f0f5')}>
                        <div style={css(v.hist.barraCorretivas)}></div>
                        <div style={css(v.hist.barraPreventivas)}></div>
                      </div>
                      <div style={css('display:flex;gap:10px;margin-top:5px')}>
                        <div style={css('display:flex;align-items:center;gap:5px')}><div style={css('width:7px;height:7px;border-radius:999px;background:#6d3ef5')}></div><div style={css('font-size:10.5px;font-weight:600;color:#5c5c70')}>{v.hist.corretivas} corretivas</div></div>
                        <div style={css('display:flex;align-items:center;gap:5px')}><div style={css('width:7px;height:7px;border-radius:999px;background:#12a45a')}></div><div style={css('font-size:10.5px;font-weight:600;color:#5c5c70')}>{v.hist.preventivas} preventivas</div></div>
                      </div>
                    </div>
                    <div className="oosv2-hist-stat-card" style={css('border:1px solid #ebebf2;border-radius:13px;padding:11px 12px;background:#fdfdff')}>
                      <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Última corretiva</div>
                      <div className="oosv2-hist-stat-value" style={css('font-size:20px;font-weight:700;letter-spacing:-0.02em;margin-top:3px')}>{v.hist.ultimaCorretiva}</div>
                      <div style={css('font-size:11px;color:#8a8a9e;font-weight:600')}>{v.hist.ultimaCorretivaData} · OS {v.hist.ultimaCorretivaOS}</div>
                    </div>
                    <div className="oosv2-hist-stat-card" style={css('border:1px solid #ebebf2;border-radius:13px;padding:11px 12px;background:#fdfdff')}>
                      <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Última preventiva</div>
                      <div className="oosv2-hist-stat-value" style={css('font-size:20px;font-weight:700;letter-spacing:-0.02em;margin-top:3px')}>{v.hist.ultimaPreventiva}</div>
                      <div style={css('font-size:11px;color:#8a8a9e;font-weight:600')}>{v.hist.ultimaPreventivaData} · OS {v.hist.ultimaPreventivaOS}</div>
                    </div>
                  </div>

                  <div className="oosv2-hist-panel" style={css('margin-top:12px;border:1px solid #ebebf2;border-radius:13px;padding:13px 14px')}>
                    <div style={css('display:flex;align-items:baseline;gap:9px;margin-bottom:12px')}>
                      <div className="oosv2-hist-panel-title" style={css('font-size:12.5px;font-weight:700')}>Linha do tempo</div>
                      <div style={css('font-size:11px;color:#8a8a9e')}>Chamados e trocas de peças no período</div>
                      <HistTip text="Período considerado no histórico">
                        <span className="oosv2-hist-period-hint oosv2-hist-period-hint--inline">
                          · {v.hist.periodo || 'últimos 24 meses'}
                        </span>
                      </HistTip>
                      <div style={css('flex:1')}></div>
                      <div style={css('display:flex;gap:11px')}>
                        <div style={css('display:flex;align-items:center;gap:5px')}><div style={css('width:7px;height:7px;border-radius:999px;background:#6d3ef5')}></div><div className="oosv2-hist-gantt-legend" style={css('font-size:10.5px;font-weight:600;color:#5c5c70')}>Corretiva</div></div>
                        <div style={css('display:flex;align-items:center;gap:5px')}><div style={css('width:7px;height:7px;border-radius:999px;background:#12a45a')}></div><div className="oosv2-hist-gantt-legend" style={css('font-size:10.5px;font-weight:600;color:#5c5c70')}>Preventiva</div></div>
                        <div style={css('display:flex;align-items:center;gap:5px')}>
                          <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M9.4 1.6a3.4 3.4 0 00-3.9 4.5L1.9 9.7a1.3 1.3 0 001.8 1.8l3.6-3.6a3.4 3.4 0 004.5-3.9L10 5.8 8.2 4 9.4 1.6z" stroke="#b5760f" strokeWidth="1.2" strokeLinejoin="round"></path></svg>
                          <div className="oosv2-hist-gantt-legend" style={css('font-size:10.5px;font-weight:600;color:#5c5c70')}>Troca de peça</div>
                        </div>
                      </div>
                    </div>

                    <div style={css('display:grid;grid-template-columns:74px 1fr;gap:9px;row-gap:6px;align-items:center')}>
                      <div style={css('font-size:10px;letter-spacing:.07em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Chamados</div>
                      <div className="oosv2-hist-gantt-lane" style={css(v.hist.linhaTempo.estiloFaixaChamados)}>
                        {(v.hist.linhaTempo.marcas || []).map((mk, i) => (
                          <div key={i} style={css(mk.linhaStyle)}></div>
                        ))}
                        {(v.hist.linhaTempo.chamados || []).map((ev, i) => (
                          <Hover
                            key={i}
                            className={ev.pillClassName}
                            onClick={ev.open}
                            baseStyle={ev.pillStyle}
                            hoverStyle="box-shadow:0 3px 10px rgba(20,20,40,.16);z-index:3"
                          >
                            <div style={css(ev.dotStyle)}></div>
                            <span className="oosv2-hist-gantt-pill-date" style={css("font-size:10px;font-weight:700;font-family:'JetBrains Mono',monospace;color:#7c7c90")}>{ev.data}</span>
                            {ev.temPeca ? (
                              <span className="oosv2-hist-gantt-pill-key" style={css(ev.chaveStyle)}>
                                <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M9.4 1.6a3.4 3.4 0 00-3.9 4.5L1.9 9.7a1.3 1.3 0 001.8 1.8l3.6-3.6a3.4 3.4 0 004.5-3.9L10 5.8 8.2 4 9.4 1.6z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"></path></svg>
                              </span>
                            ) : null}
                          </Hover>
                        ))}
                      </div>

                      <div></div>
                      <div className="oosv2-hist-gantt-axis" style={css('position:relative;height:14px')}>
                        {(v.hist.linhaTempo.marcas || []).map((mk, i) => (
                          <div key={i} className="oosv2-hist-gantt-mark" style={css(mk.rotuloStyle)}>{mk.label}</div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="oosv2-hist-panel" style={css('margin-top:12px;border:1px solid #ebebf2;border-radius:13px;padding:13px 14px')}>
                    <div style={css('display:flex;align-items:baseline;gap:9px;margin-bottom:11px')}>
                      <div className="oosv2-hist-panel-title" style={css('font-size:12.5px;font-weight:700')}>{cutover ? 'Serviços mais frequentes' : 'Defeitos mais frequentes'}</div>
                      <div style={css('font-size:11px;color:#8a8a9e')}>Pareto acumulado — o realce cobre 80% das ocorrências</div>
                      <div style={css('flex:1')}></div>
                      <div style={css('font-size:11px;color:#8a8a9e;font-weight:600')}>{v.hist.periodo}</div>
                    </div>
                    <div style={css('display:flex;flex-direction:column;gap:7px')}>
                      {(v.hist.pareto || []).map((p, i) => (
                        <div key={i} style={css('display:grid;grid-template-columns:210px 1fr 44px 58px;gap:10px;align-items:center')}>
                          <div className={"oosv2-hist-pareto-label" + (p.highlight ? " is-highlight" : "")} style={css(p.labelStyle)}>{p.defeito}</div>
                          <div className="oosv2-hist-pareto-track" style={css('height:16px;border-radius:6px;background:#f4f4f8;overflow:hidden')}>
                            <div style={css(p.barStyle)}></div>
                          </div>
                          <div className="oosv2-hist-pareto-qtd" style={css("font-size:11.5px;font-weight:700;font-family:'JetBrains Mono',monospace;text-align:right")}>{p.qtd}</div>
                          <div className={"oosv2-hist-pareto-acum" + (p.highlight ? " is-highlight" : "")} style={css(p.acumStyle)}>{p.acumulado}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="oosv2-prod-card" style={css('margin-top:12px;border:1px solid #ebebf2;border-radius:13px;padding:13px 14px')}>
                    <div style={css('display:flex;align-items:center;gap:9px;margin-bottom:8px;flex-wrap:wrap')}>
                      <div className="oosv2-hist-panel-title" style={css('font-size:12.5px;font-weight:700')}>Produção do equipamento</div>
                      <div style={css('font-size:11px;color:#8a8a9e')}>Últimos 6 meses</div>
                      <div style={css('flex:1')}></div>
                      {!v.hist.producao.semProducao ? (
                        <div style={css('display:flex;gap:5px;flex-wrap:wrap')}>
                          {(v.hist.producao.legenda || []).map((lg, i) => (
                            <div
                              key={i}
                              onClick={lg.static ? undefined : lg.toggle}
                              className={
                                "oosv2-hist-prod-legend" +
                                (lg.on ? " is-on" : "") +
                                (lg.static ? " is-static" : "")
                              }
                              style={css(lg.style)}
                            >
                              <div
                                className={lg.dotClass || undefined}
                                style={lg.dotStyle ? css(lg.dotStyle) : undefined}
                              ></div>
                              {lg.label}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    {v.hist.producao.semProducao ? (
                      <div className="oosv2-recharts-empty" role="status">
                        <div className="oosv2-recharts-empty-icon" aria-hidden="true">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                            <path d="M4 19V5M4 19h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                            <path d="M8 15v-3M12 15V9M16 15v-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                          </svg>
                        </div>
                        <p className="oosv2-recharts-empty-title">Sem produção no período</p>
                        <p className="oosv2-recharts-empty-text">Não há produção registrada neste período</p>
                      </div>
                    ) : (
                      <>
                        <div id="oosv2-prod-recharts-host" className="oosv2-recharts-host"></div>
                        {(v.hist.producao.medias || []).length > 0 ? (
                          <div className="oosv2-prod-stats">
                            <div className="oosv2-prod-stats-medias">
                              <span className="oosv2-prod-stats-label">Média mensal</span>
                              {(v.hist.producao.medias || []).map((m) => (
                                <span key={m.key} className="oosv2-prod-stats-chip">
                                  <span className="oosv2-prod-stats-dot" style={{ background: m.cor }} />
                                  <span className="oosv2-prod-stats-chip-label">{m.label}</span>
                                  <span className="oosv2-prod-stats-chip-val">{m.mediaLabel}</span>
                                </span>
                              ))}
                            </div>
                            {v.hist.producao.comparativo ? (
                              <div className="oosv2-prod-stats-compare">
                                <span>
                                  Total de cópias <strong>{v.hist.producao.comparativo.totalLabel}</strong>
                                </span>
                                <span className="oosv2-prod-stats-sep" aria-hidden="true">·</span>
                                <span>
                                  Média mensal <strong>{v.hist.producao.comparativo.mediaMensalLabel}</strong>
                                </span>
                                {v.hist.producao.comparativo.cicloMensalLabel ? (
                                  <>
                                    <span className="oosv2-prod-stats-sep" aria-hidden="true">·</span>
                                    <span>
                                      Ciclo <strong>{v.hist.producao.comparativo.cicloMensalLabel}</strong>
                                    </span>
                                  </>
                                ) : null}
                                {v.hist.producao.comparativo.pctLabel ? (
                                  <>
                                    <span className="oosv2-prod-stats-sep" aria-hidden="true">·</span>
                                    <span
                                      className={
                                        "oosv2-prod-stats-pct" +
                                        (v.hist.producao.comparativo.acimaMedia
                                          ? " is-up"
                                          : " is-down")
                                      }
                                    >
                                      <strong>{v.hist.producao.comparativo.pctLabel}</strong>{" "}
                                      {v.hist.producao.comparativo.texto}
                                    </span>
                                  </>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </>
              ) : null}

              {v.abaOS ? (
                <>
                <HistTip text="Período considerado no histórico">
                  <div className="oosv2-hist-period-hint">
                    Exibindo {v.hist.periodo || 'últimos 24 meses'}
                  </div>
                </HistTip>
                <div className="oosv2-hist-table-wrap" style={css('border:1px solid #ebebf2;border-radius:13px;overflow:auto')}>
                  <div className="oosv2-hist-table-head" style={css(v.gradeCabecalho)}>
                    <div style={css('font-size:9.5px;letter-spacing:.07em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>OS</div>
                    {(v.colunasAtivas || []).map((col, i) => (
                      <div key={i} style={css(col.headStyle)}>{colLabel(col.label)}</div>
                    ))}
                  </div>
                  {(v.hist.ordens || []).map((o, oi) => (
                    <div key={oi} className="oosv2-hist-table-group" style={css('border-bottom:1px solid #f4f4f8;min-width:fit-content')}>
                      <div onClick={o.toggle} className={"oosv2-hist-table-row" + (o.aberto ? " is-open" : "")} style={css(o.rowStyle)}>
                        <div style={css('display:flex;flex-wrap:wrap;align-items:center;align-content:center;gap:4px 5px;min-width:0;max-width:100%')}>
                          <div style={css(o.caretStyle)}>
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M4.2 2.6L8 6l-3.8 3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                          </div>
                          <div className="oosv2-hist-os-num" style={css("font-size:11.5px;font-weight:700;font-family:'JetBrains Mono',monospace;color:#5a2ee0")}>{o.numero}</div>
                          {o.temPeca ? (
                            <HistTip text="Peças trocadas">
                              <span className="oosv2-os-badge oosv2-os-badge--peca">P</span>
                            </HistTip>
                          ) : null}
                          {o.reincidente ? (
                            <HistTip text={`Reincidência · OS ${o.reincidenteOs}`}>
                              <span className="oosv2-os-badge oosv2-os-badge--reinc">R</span>
                            </HistTip>
                          ) : null}
                          {o.returnCount > 0 ? (
                            <HistTip
                              wrap
                              text={cutover
                                ? `OS com ${o.ciclos?.length || o.returnCount} ciclo(s) · expanda para ver cada um`
                                : `OS com ${o.returnCount} retorno(s) · expanda para ver cada ciclo`}
                            >
                              <span className="oosv2-os-badge oosv2-os-badge--ret">
                                {cutover ? `Ciclos: ${o.ciclos?.length || o.returnCount}` : `Retornos: ${o.returnCount}`}
                              </span>
                            </HistTip>
                          ) : null}
                        </div>
                        {(o.celulas || []).map((c, ci) => (
                          <div key={ci} style={css(c.cellStyle)}>
                            {c.temAlerta ? (
                              <span className="oosv2-alert-code" style={css(c.alertaStyle)}>{c.alerta}</span>
                            ) : null}
                            <span className={"oosv2-ellip oosv2-hist-cell-val" + (c.cellClassName ? " " + c.cellClassName : "")} style={css(c.valorStyle)}>{c.valor}</span>
                            <span style={css(c.deltaStyle)}>{c.delta}</span>
                          </div>
                        ))}
                      </div>
                      {o.aberto ? (
                        <div className="oosv2-hist-table-detail" style={css('padding:11px 14px 13px;background:#fbfbfe;border-top:1px solid #f2f2f7;animation:popIn .14s ease both')}>
                          <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Motivo</div>
                          <div className="oosv2-hist-detail-text" style={css('font-size:12.5px;margin-top:2px')}>{o.motivo}</div>

                          <div className="oosv2-hist-detail-sep" style={css('height:1px;background:#ececf2;margin:10px 0')}></div>
                          <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Laudo técnico</div>
                          <div className="oosv2-hist-detail-text oosv2-hist-detail-muted" style={css('font-size:12.5px;line-height:1.55;margin-top:2px;color:#3c3c4c')}>{o.laudo}</div>

                          {(o.ciclos || []).length ? (
                            <>
                              <div className="oosv2-hist-detail-sep" style={css('height:1px;background:#ececf2;margin:10px 0')}></div>
                              <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700;margin-bottom:6px')}>Ciclos</div>
                              {cutover ? (
                                <HistCiclos
                                  ciclos={o.ciclos}
                                  returnCount={o.returnCount}
                                  numeroOS={o.numero}
                                  printBase={{
                                    tipo: o.tipo,
                                    cliente: o.cliente,
                                    equipamento: v.equip && v.equip.modelo,
                                    serie: v.equip && v.equip.serie,
                                    patrimonio: v.equip && v.equip.patrimonio,
                                    defeito: o.defeito,
                                    motivo: o.motivo,
                                    abertura: o.aberturaImpressao && o.aberturaImpressao !== '—' ? o.aberturaImpressao : o.abertura,
                                  }}
                                />
                              ) : (
                              <div className="oosv2-hist-subtable" style={css('border:1px solid #ececf2;border-radius:9px;overflow:hidden;background:#fff')}>
                                <div className="oosv2-hist-subtable-head" style={css('display:grid;grid-template-columns:140px 1fr 1fr 90px 90px;gap:8px;padding:6px 10px;background:#f7f7fa;border-bottom:1px solid #ececf2')}>
                                  {['Ciclo', 'Condição', 'Técnico', 'Fechamento', 'A4 P&B'].map((h) => (
                                    <div key={h} style={css('font-size:9.5px;letter-spacing:.06em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>{h}</div>
                                  ))}
                                </div>
                                {o.ciclos.map((c) => (
                                  <div key={c.label} className="oosv2-hist-subtable-row" style={css('display:grid;grid-template-columns:140px 1fr 1fr 90px 90px;gap:8px;padding:7px 10px;border-bottom:1px solid #f6f6fa')}>
                                    <div style={css('font-size:11.5px;font-weight:700')}>{c.label}</div>
                                    <div className="oosv2-ellip" style={css('font-size:11.5px;font-weight:600')}>{c.condicao}</div>
                                    <div className="oosv2-ellip oosv2-hist-cell-muted" style={css('font-size:11.5px;color:#4a4a5c')}>{c.tecnico}</div>
                                    <div className="oosv2-hist-cell-mono" style={css("font-size:11.5px;font-family:'JetBrains Mono',monospace")}>{c.fechamento}</div>
                                    <div className="oosv2-hist-cell-mono" style={css("font-size:11.5px;font-family:'JetBrains Mono',monospace")}>{c.contadorPB}</div>
                                  </div>
                                ))}
                              </div>
                              )}
                            </>
                          ) : null}

                          {cutover && (o.ciclos || []).length ? null : (
                            <OpenOsV2HistOsGoMedia numeroOS={o.numero} />
                          )}

                          {o.temPeca ? (
                            <>
                              <div className="oosv2-hist-detail-sep" style={css('height:1px;background:#ececf2;margin:10px 0')}></div>
                              <div style={css('font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9e;font-weight:700;margin-bottom:6px')}>Peças trocadas</div>
                              <div className="oosv2-hist-subtable" style={css('border:1px solid #ececf2;border-radius:9px;overflow:hidden;background:#fff')}>
                                <div className="oosv2-hist-subtable-head" style={css('display:grid;grid-template-columns:90px 110px 1fr 56px;gap:8px;padding:6px 10px;background:#f7f7fa;border-bottom:1px solid #ececf2')}>
                                  <div style={css('font-size:9.5px;letter-spacing:.06em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Código</div>
                                  <div style={css('font-size:9.5px;letter-spacing:.06em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Referência</div>
                                  <div style={css('font-size:9.5px;letter-spacing:.06em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Produto</div>
                                  <div style={css('font-size:9.5px;letter-spacing:.06em;text-transform:uppercase;color:#8a8a9e;font-weight:700;text-align:right')}>Qtde</div>
                                </div>
                                {(o.pecas || []).map((pt, pi) => (
                                  <div key={pi} className="oosv2-hist-subtable-row" style={css('display:grid;grid-template-columns:90px 110px 1fr 56px;gap:8px;padding:7px 10px;border-bottom:1px solid #f6f6fa')}>
                                    <div className="oosv2-hist-cell-mono" style={css("font-size:11.5px;font-family:'JetBrains Mono',monospace")}>{pt.codigo}</div>
                                    <div className="oosv2-hist-cell-mono oosv2-hist-cell-muted" style={css("font-size:11.5px;font-family:'JetBrains Mono',monospace;color:#4a4a5c")}>{pt.referencia}</div>
                                    <div className="oosv2-ellip" style={css('font-size:11.5px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis')}>{pt.produto}</div>
                                    <div style={css("font-size:11.5px;font-weight:700;font-family:'JetBrains Mono',monospace;text-align:right")}>{pt.qtde}</div>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
                </>
              ) : null}

              {v.abaPecas ? (
                <div className="oosv2-hist-table-wrap" style={css('border:1px solid #ebebf2;border-radius:13px;overflow:auto')}>
                  <div className="oosv2-hist-table-head" style={css(v.gradePecasCabecalho)}>
                    <div style={css('font-size:9.5px;letter-spacing:.07em;text-transform:uppercase;color:#8a8a9e;font-weight:700')}>Pedido</div>
                    {(v.colunasPecasAtivas || []).map((col, i) => (
                      <div key={i} style={css(col.headStyle)}>{col.label}</div>
                    ))}
                  </div>
                  {(v.hist.pecas || []).map((pc, pci) => (
                    <div key={pci} className="oosv2-hist-table-row" style={css(pc.rowStyle)}>
                      <div className="oosv2-hist-os-num" style={css("font-size:11.5px;font-weight:700;font-family:'JetBrains Mono',monospace;color:#5a2ee0")}>{pc.pedido}</div>
                      {(pc.celulas || []).map((c, ci) => (
                        <div key={ci} style={css(c.cellStyle)}>
                          <span className={"oosv2-ellip oosv2-hist-cell-val" + (c.cellClassName ? " " + c.cellClassName : "")} style={css(c.valorStyle)}>{c.valor}</span>
                          <span className="oosv2-ellip oosv2-hist-cell-sub" style={css(c.subStyle)}>{c.sub}</span>
                          <span style={css(c.deltaStyle)}>{c.delta}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

          </div>
          {v.ganttTipAberto ? (
            <div className="oosv2-gantt-tip" style={css(v.ganttTip.style)} onClick={v.keepGanttTip}>
              <div className="oosv2-gantt-tip-os">
                <span>OS {v.ganttTip.os}</span>
                {v.ganttTip.temReincidencia ? (
                  <HistTip text="Reincidência">
                    <span className="oosv2-gantt-tip-reinc">↻ {v.ganttTip.reincidencia}</span>
                  </HistTip>
                ) : null}
              </div>
              <div className="oosv2-gantt-tip-row">
                <span className="oosv2-gantt-tip-label">{cutover ? 'Serviços' : 'Defeito'}</span>
                <span className="oosv2-gantt-tip-val">{v.ganttTip.defeito}</span>
              </div>
              <div className="oosv2-gantt-tip-row">
                <span className="oosv2-gantt-tip-label">Técnico</span>
                <span className="oosv2-gantt-tip-val">{v.ganttTip.tecnico}</span>
              </div>
              <div className="oosv2-gantt-tip-row">
                <span className="oosv2-gantt-tip-label">Condição</span>
                <span className="oosv2-gantt-tip-val">{v.ganttTip.condicao}</span>
              </div>
              {v.ganttTip.temPecas ? (
                <div className="oosv2-gantt-tip-pecas">
                  <div className="oosv2-gantt-tip-pecas-title">Peças trocadas</div>
                  {(v.ganttTip.pecas || []).map((pc, i) => (
                    <div key={i} className="oosv2-gantt-tip-peca">
                      <span className="oosv2-gantt-tip-peca-nome">{pc.nome}</span>
                      <span className="oosv2-gantt-tip-peca-qtde">× {pc.qtde}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          <OpenOsV2ProdStickNote
            open={
              !!(v.abaOS || v.abaPecas) &&
              (v.hist?.producao?.medias || []).length > 0 &&
              !!v.hist?.producao?.comparativo?.cicloMensalLabel
            }
            medias={v.hist?.producao?.medias || []}
            comparativo={v.hist?.producao?.comparativo || null}
            anchorRef={histDialogRef}
          />
        </div>
      ) : null}

      {v.temToast ? (
        <div style={css('position:fixed;bottom:18px;left:50%;transform:translateX(-50%);background:#16161d;color:#fff;font-size:12.5px;font-weight:600;padding:9px 15px;border-radius:10px;box-shadow:0 8px 24px rgba(10,10,25,.45);z-index:60;animation:popIn .16s ease both')}>{v.toast}</div>
      ) : null}
      {v.guardAlert ? (
        <div className="oosv2-guard-overlay" role="dialog" aria-modal="true" aria-label="Aviso de abertura">
          <div className="oosv2-guard-card">
            {(v.guardAlert.osNumbers || []).length || (v.guardAlert.osAbertas || []).length ? (
              <div className="oosv2-guard-block">
                <div className="oosv2-guard-title">Ordem(ns) de serviço em aberto</div>
                <div className="oosv2-guard-text">
                  {v.isMultipla && Array.isArray(v.guardAlert.osAbertas) && v.guardAlert.osAbertas.length ? (
                    <>
                      {v.guardAlert.osAbertas.length === 1
                        ? "Já existe ordem de serviço em aberto para o equipamento abaixo:"
                        : "Já existem ordens de serviço em aberto para os equipamentos abaixo:"}
                      <ul className="oosv2-guard-list">
                        {v.guardAlert.osAbertas.map((item) => (
                          <li key={String(item.serie) + "::" + String(item.osNumber)}>
                            Série{' '}
                            <span className="oosv2-guard-code is-static">{item.serie}</span>
                            {item.patrimonio && item.patrimonio !== "—" ? (
                              <>
                                {' · Patrimônio '}
                                <span className="oosv2-guard-code is-static">{item.patrimonio}</span>
                              </>
                            ) : null}
                            {' — OS '}
                            <button
                              type="button"
                              className="oosv2-guard-code"
                              title="Ver detalhes desta OS"
                              onClick={() => v.verOsGuard(item.osNumber)}
                            >
                              {item.osNumber}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <>
                      Já existe(m) ordem(ns) de serviço em aberto para este equipamento:{' '}
                      {(v.guardAlert.osNumbers || []).map((num, idx) => (
                        <React.Fragment key={String(num)}>
                          {idx > 0 ? <span aria-hidden>, </span> : null}
                          <button
                            type="button"
                            className="oosv2-guard-code"
                            title="Ver detalhes desta OS"
                            onClick={() => v.verOsGuard(num)}
                          >
                            {num}
                          </button>
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </div>
                <div className="oosv2-guard-hint">Não é possível prosseguir com a abertura de nova OS.</div>
              </div>
            ) : null}
            {((v.guardAlert.retiradas && v.guardAlert.retiradas.length) || (v.guardAlert.retiradaCodigos || []).length) ? (
              <div className="oosv2-guard-block">
                <div className="oosv2-guard-title">Retirada em aberto</div>
                <div className="oosv2-guard-text">
                  {(Array.isArray(v.guardAlert.retiradas) && v.guardAlert.retiradas.length)
                    ? (v.guardAlert.retiradas.length === 1
                      ? (
                        <>
                          O equipamento série{' '}
                          <span className="oosv2-guard-code is-static">{v.guardAlert.retiradas[0].serie}</span>
                          {' '}possui retirada ativa em andamento. Código da retirada:{' '}
                          <span className="oosv2-guard-code is-static">{v.guardAlert.retiradas[0].codigo}</span>
                        </>
                      )
                      : (
                        <>
                          Os equipamentos abaixo possuem retirada ativa em andamento:
                          <ul className="oosv2-guard-list">
                            {v.guardAlert.retiradas.map((item) => (
                              <li key={String(item.serie) + "::" + String(item.codigo)}>
                                Série{' '}
                                <span className="oosv2-guard-code is-static">{item.serie}</span>
                                {' — código '}
                                <span className="oosv2-guard-code is-static">{item.codigo}</span>
                              </li>
                            ))}
                          </ul>
                        </>
                      ))
                    : (
                      <>
                        Este equipamento possui retirada ativa em andamento. Código da retirada:{' '}
                        {(v.guardAlert.retiradaCodigos || []).map((cod, idx) => (
                          <React.Fragment key={String(cod)}>
                            {idx > 0 ? <span aria-hidden>, </span> : null}
                            <span className="oosv2-guard-code is-static">{cod}</span>
                          </React.Fragment>
                        ))}
                      </>
                    )}
                </div>
                <div className="oosv2-guard-hint">Não é possível prosseguir com a abertura de nova OS sem confirmar.</div>
              </div>
            ) : null}
            {(v.guardAlert.reincidencias || []).length ? (
              <div className="oosv2-guard-block">
                <div className="oosv2-guard-title">Reincidência</div>
                <div className="oosv2-guard-text">
                  <ul className="oosv2-guard-list">
                    {v.guardAlert.reincidencias.map((item) => (
                      <li key={String(item.serie) + "::" + String(item.osNumber)}>
                        {v.isMultipla ? (
                          <>
                            Série{' '}
                            <span className="oosv2-guard-code is-static">{item.serie}</span>
                            {' — '}
                          </>
                        ) : null}
                        OS anterior nº{' '}
                        <span className="oosv2-guard-code is-static">{item.osNumber}</span>
                        {item.closedAt
                          ? `, fechada em ${new Date(item.closedAt).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}`
                          : null}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="oosv2-guard-hint">
                  A nova OS só será registrada como reincidência se o tipo escolhido for {recurrenceTypePhrase(v.guardAlert.reincidencias[0]?.typeLabels)}.
                </div>
              </div>
            ) : null}
            <div className="oosv2-guard-actions">
              <button type="button" className="oosv2-guard-cancel" onClick={v.cancelarGuard}>Cancelar</button>
              <button type="button" className="oosv2-guard-continue" onClick={v.continuarGuard}>Continuar</button>
            </div>
          </div>
        </div>
      ) : null}
      <OpenOsV2EmailModal v={v} />
      <OpenOsV2ContratoVencModal v={v} />
      <OpenOsV2RecentOsModal v={v} />
    </div>
  );
}
